/* ============================================================================
 * ez-configurator-shade.js — the EZ-Snap "Shade Finder" island (vanilla JS).
 *
 * Ports demo/app/products/exterior-shade/{ShadePDP,ShadeConfigurator}.jsx to a
 * framework-free island that mounts inside sections/ez-configurator-shade.liquid.
 *
 * STEP FLOW (crown jewel — max care with step logic):
 *   1  Application picker (dark showcase; rv / window / skylight / boat)
 *   2  Mesh colour            (BK / WH — applies to the whole order)
 *   3  Windows                (per-window W×H×qty + type → fabric size + snaps)
 *   4  Format                 (precut vs roll — live price compare, availability gating)
 *   5  Add-ons                (body-colour studs, storage bag, dome Airflo spacers)
 *   6  Your order             (line list, roll CUT-PATTERN SVG, total, multi-line ATC)
 *
 * DATA: the section injects a catalog JSON (built by Liquid from a product_list) into
 *   window.__EZ_SHADE_CFG__[sectionId] = { catalog, apps, ui, images, links }.
 *   PRICES + VARIANT IDS come from that catalog — never typed here. The pure math is the
 *   shared window.EZSnap.createShadeEngine + createNestEngine engines (already gated).
 *
 * PERSISTENCE: localStorage per application (ezsnap_shade_<app>), like the demo — AND the current
 *   build is mirrored to the URL (?app&c&w&fmt&st&bg&af) via history.replaceState, so an emailed /
 *   shared link reopens the exact build. Matches the skirting + hitch configurators (all 3 URL-save).
 * CART: a single multi-line POST to /cart/add.js (Shopify AJAX), then → /cart.
 * ============================================================================ */
(function () {
  "use strict";

  var MONEY_FMT = null; // set from injected config (money_format) at boot per instance
  // ATC all-or-nothing error (N1 missing variant, or a precut kit snap pack that can't be resolved).
  var ATC_PARTIAL_MSG = "One item in your build is temporarily unavailable, so nothing was added: we won't ship a partial order. Please refresh, or contact us and we'll place it for you.";

  // ---- currency (Canada market sells in CAD). window.EZ (layout/theme.liquid) is the ONE theme currency
  //      context: { ccy, isBase, fs, money() }. The section config's currency/baseCurrency is only a
  //      fallback for a page where the layout script is absent. Prices in the catalog are ALREADY in the
  //      shopper's currency (Liquid injects presentment prices) — this only decides format + shipping claims.
  var CFG_CCY = null; // { ccy, base } from the injected config, set at boot
  function ccyCtx() {
    var E = window.EZ;
    if (E && typeof E.isBase === "boolean") return E;
    if (CFG_CCY && CFG_CCY.ccy && CFG_CCY.base) return { ccy: CFG_CCY.ccy, isBase: CFG_CCY.ccy === CFG_CCY.base };
    return null;
  }
  // true for the store currency (USD) — AND when nothing says otherwise, so USD behaviour is the default.
  function isBaseCcy() { var c = ccyCtx(); return !c || c.isBase; }
  function ccyCode() { var c = ccyCtx(); return (c && c.ccy) || "USD"; }
  // Free-shipping threshold label. Shipping rates are USD (converted at checkout), so a non-USD shopper
  // sees it in US dollars ("US$225"). EZ.fs comes from the same metafield as the [[fs]] copy token; an
  // unresolved token (no digits) falls back to the configured threshold.
  function fsLabel(over) {
    var E = window.EZ;
    if (E && typeof E.fs === "string" && /\d/.test(E.fs)) return E.fs;
    return (isBaseCcy() ? "$" : "US$") + over;
  }

  // ---- money: mirrors demo money() = "$" + toLocaleString, integers show no cents ----
  function money(n) {
    n = Number(n) || 0;
    if (!isBaseCcy()) {
      // non-USD: "CA$1,292" / "CA$28.63" — the same string the Liquid ez-money snippet prints
      if (window.EZ && typeof window.EZ.money === "function") return window.EZ.money(n);
      var c = ccyCode(), whole = Math.round(n * 100) % 100 === 0;
      return (c === "CAD" ? "CA$" : c + " ") + n.toLocaleString("en-US", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
    }
    var opts = { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 };
    return "$" + n.toLocaleString("en-US", opts);
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  // Resolve the Klaviyo public company id from the onsite embed already on the page (its loader /
  // forms / fonts URLs all carry ?company_id=<id> or /<id>/). No hardcoded key — portable per store.
  function klaviyoCompanyId() {
    try {
      // ONLY trust an explicit ?company_id= — the onsite loader always carries it. A looser
      // path-segment fallback was dropped: it could grab an arbitrary 6-char segment ("onsite",
      // "static") off a company_id-less Klaviyo asset and fire the event to a bogus company.
      var nodes = document.querySelectorAll('script[src*="klaviyo"],link[href*="klaviyo"]');
      for (var i = 0; i < nodes.length; i++) {
        var u = nodes[i].getAttribute("src") || nodes[i].getAttribute("href") || "";
        var m = u.match(/[?&]company_id=([A-Za-z0-9]{5,})/);
        if (m) return m[1];
      }
    } catch (e) {}
    return null;
  }

  // Fire the "Submitted Configuration" event that powers the transactional build-recap flow.
  // IMPORTANT: window.klaviyo.track() does NOT deliver on this store — onsite behavioural tracking
  // runs through a third party, so Klaviyo's native onsite queue never flushes (verified: events
  // never landed). The reliable, CORS-free path is the legacy /api/track image beacon (the same
  // transport klaviyo.js uses under the hood; no preflight). identify() is still called when the
  // onsite object exists, purely to tie the browser cookie to the email — it is not relied upon.
  function klaviyoTrack(event, email, props) {
    try {
      var token = klaviyoCompanyId();
      if (!token) { if (window.console) console.warn("[ez-shade] no Klaviyo company id found on page"); return false; }
      try { if (window.klaviyo && window.klaviyo.identify) window.klaviyo.identify({ email: email, $email: email }); } catch (e) {}
      var payload = { token: token, event: event, customer_properties: { $email: email }, properties: props || {} };
      var b64 = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
      new Image().src = "https://a.klaviyo.com/api/track?data=" + encodeURIComponent(b64);
      return true;
    } catch (e) { if (window.console) console.warn("[ez-shade] klaviyo track failed", e); return false; }
  }

  // Mirror the emailed quote into HubSpot (contact + quote props + browsing-stitch). Additive to the
  // Klaviyo recap above; fire-and-forget with a silent catch so it never blocks the email/UX.
  function hubspotQuoteSubmit(email, props) {
    try {
      var hutk = (document.cookie.match(/hubspotutk=([^;]+)/) || [])[1] || '';
      var summary = (props.specs || []).map(function (s) { return s.label + ': ' + s.value; }).join(' · ') || props.summary || '';
      var fields = [
        { name: 'email', value: email },
        { name: 'quoted_product', value: props.config_type || '' },
        { name: 'quoted_kit', value: props.kit || props.format || props.application || '' },
        { name: 'last_quote_total', value: (props.total != null ? String(props.total) : '') },
        { name: 'rv_make_and_model', value: props.rv || '' },
        { name: 'quote_summary', value: summary.slice(0, 600) },
        { name: 'last_quote_date', value: String(Date.now()) }
      ].filter(function (f) { return f.value !== ''; });
      fetch('https://api.hsforms.com/submissions/v3/integration/submit/7022036/6451d33b-f8cf-4cba-bf07-8f429dc6c19c', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submittedAt: Date.now(), fields: fields, context: { hutk: hutk, pageUri: window.location.href, pageName: document.title } })
      }).catch(function () {});
    } catch (e) {}
  }

  // =========================================================================
  // One island instance.
  // =========================================================================
  function ShadeFinder(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.sid = cfg.sectionId;
    this.apps = cfg.apps || [];               // [{ id, label, item, types:[{id,label,perSide,dome?,affix?,tip}] }]
    this.appMeta = cfg.appMeta || {};         // id -> { label, stat, pitch, img, photos:[...] }
    this.wtypeThumb = cfg.wtypeThumb || {};   // appId -> { typeId: url }
    this.studHw = cfg.studHw || [];           // [{ code, name, c }]
    this.colors = cfg.colors || [];           // [{ code, name, note, popular? }]
    this.acc = cfg.accessories || {};         // { storageBag:{sku,price,variantId,name}, airflo:{...} }
    this.links = cfg.links || {};
    this.copy = cfg.copy || {};
    if (cfg.currency && cfg.baseCurrency) CFG_CCY = { ccy: cfg.currency, base: cfg.baseCurrency }; // fallback only (see ccyCtx)

    // engines from the injected catalog
    var EZ = window.EZSnap || {};
    this.shade = EZ.createShadeEngine ? EZ.createShadeEngine(cfg.catalog || {}) : null;
    // [40ft roll — NEVER-PRICIER best-of] A longer roll must never make a build cost MORE than today's
    // ≤25ft set would. So we plan BOTH ways — the full roll set (incl. any 40ft) and the "capped" ≤25ft
    // set (= today's live behavior) — and return the CHEAPER. Never pricier BY CONSTRUCTION; the full
    // set's higher ceiling (40ft) also ENABLES single shades 25-40ft that are oversize today. Wrapper is
    // configurator-level, so ez-engine-nest.js + the demo engine are UNCHANGED (behavior-gate untouched).
    var CAP_FT = 25; // today's live max roll = the never-pricier baseline
    var mkNest = function (shadeEng, fts) {
      return (EZ.createNestEngine && shadeEng)
        ? EZ.createNestEngine({ rollWidthIn: cfg.rollWidthIn || 72, rollPlan: shadeEng.rollPlan, rollLengthsFt: fts })
        : null;
    };
    var allRolls = (this.shade && this.shade.catalog && this.shade.catalog.rolls) || [];
    var fullFt = allRolls.map(function (r) { return r.ft; });
    var cappedFt = fullFt.filter(function (ft) { return ft <= CAP_FT; });
    var cappedMax = cappedFt.length ? Math.max.apply(null, cappedFt) : CAP_FT;
    var ftPrice = {};
    allRolls.forEach(function (r) { if (ftPrice[r.ft] == null) ftPrice[r.ft] = r.price; });
    var rollCost = function (rolls) { var s = 0, i; for (i = 0; i < (rolls || []).length; i++) s += (ftPrice[rolls[i]] || 0); return s; };
    var nestFull = mkNest(this.shade, fullFt);
    // capped engine = today's ≤25ft behavior (from a 40-filtered shade engine so its rollPlan matches).
    // Only built when the catalog actually carries a roll longer than the cap, else the best-of is a no-op.
    var nestCapped = null;
    if (EZ.createShadeEngine && cfg.catalog && fullFt.length > cappedFt.length) {
      var cappedCatalog = Object.assign({}, cfg.catalog, { rolls: (cfg.catalog.rolls || []).filter(function (r) { return r.ft <= CAP_FT; }) });
      nestCapped = mkNest(EZ.createShadeEngine(cappedCatalog), cappedFt);
    }
    this.nest = nestFull
      ? Object.assign({}, nestFull, {
          nestPanels: function (panels) {
            var full = nestFull.nestPanels(panels);
            if (!nestCapped || !full || full.oversize || !full.rolls || !full.rolls.length) return full;
            var usedBig = false, i;
            for (i = 0; i < full.rolls.length; i++) { if (full.rolls[i] > cappedMax + 1e-9) { usedBig = true; break; } }
            if (!usedBig) return full;                       // no long roll chosen → identical to capped
            var capped = nestCapped.nestPanels(panels);
            if (!capped || capped.oversize || !capped.rolls || !capped.rolls.length) return full; // enablement: capped can't build
            var cf = rollCost(full.rolls), cc = rollCost(capped.rolls);
            if (cc < cf - 0.01) return capped;               // capped cheaper → today's plan wins (NEVER pricier)
            if (cf < cc - 0.01) return full;                 // full cheaper → the 40ft actually saves money
            return capped.rolls.length < full.rolls.length ? capped : full; // cost tie → fewer rolls, else the 40ft
          },
        })
      : null;

    // ---- state ----
    this.app = null;
    this.color = (this.colors[0] && this.colors[0].code) || "BK";
    this.windows = [];      // [{ w, h, qty, type }]
    this.winOpen = 0;
    this.style = null;      // 'precut' | 'roll'
    this.studsOn = false;
    this.studColor = "BK";
    this.bagQty = 0;
    this.airOn = false;
    this.open = 2;          // accordion step 2..6
    this.activePhoto = 0;

    // ── directed mode (per-application PDP): app route-locked, picker dropped, product header +
    //    app-switcher chips + gallery-left. Mirrors the demo ShadeConfigurator `directed` prop. ──
    this.directed = !!cfg.directed;
    this.header = cfg.header || null;
    if (this.directed && cfg.app) this.app = cfg.app;
    // Initial restore: a shared / emailed build link (?app&c&w&fmt…) wins over the local
    // localStorage so the exact build reopens; otherwise fall back to session (directed default).
    var fromUrl = this.hasUrlState();
    if (fromUrl) this.restoreFromUrl();
    else if (this.app) this.restore();

    this.render();
    // Email-resume: when the build came from the URL, anchor to the open builder so the customer
    // lands on their saved build — not the app-picker cards above it on the master page, which they
    // might otherwise re-pick from and lose their place.
    if (fromUrl && this.app) this.scrollToBuilder(300);
  }

  ShadeFinder.prototype.appObj = function () {
    var a = this.app;
    return this.apps.find(function (x) { return x.id === a; }) || null;
  };

  ShadeFinder.prototype.colorName = function () {
    var c = this.color;
    var m = this.colors.find(function (x) { return x.code === c; });
    return (m && m.name) || "Black";
  };

  ShadeFinder.prototype.newWin = function (typeId) {
    return { w: "", h: "", qty: "1", type: typeId };
  };

  // ---- derivations (mirror ShadeConfigurator useMemo/useMemo chains) ----
  ShadeFinder.prototype.derive = function () {
    var self = this, ao = this.appObj();
    var types = (ao && ao.types) || [];
    var wins = (this.windows || []).map(function (win, i) {
      var type = types.find(function (t) { return t.id === win.type; }) || types[0];
      var W = Number(win.w) || 0, H = Number(win.h) || 0, qty = Math.max(1, Math.floor(Number(win.qty) || 1)); // N3: integer qty only (no fractional panels/price/cart)
      var valid = W > 0 && H > 0 && !!type;
      var fab = valid ? self.shade.fabricSize(W, H, type.perSide) : null;
      var snaps = fab ? self.shade.precutSnaps(fab.w, fab.h) : 0;
      var match = fab ? self.shade.precutMatch(fab.w, fab.h) : null;
      return { i: i, W: W, H: H, qty: qty, type: type, valid: valid, fab: fab, snaps: snaps,
        match: match, dome: !!(type && type.dome), name: "Window " + (i + 1) };
    });
    var validWins = wins.filter(function (w) { return w.valid; });
    var allValid = validWins.length > 0;
    var precutAvail = allValid && validWins.every(function (w) { return w.match; });
    var precutTotal = precutAvail ? validWins.reduce(function (s, w) { return s + w.match.price * w.qty; }, 0) : 0;
    var totalSnaps = validWins.reduce(function (s, w) { return s + w.snaps * w.qty; }, 0);

    var panels = [];
    if (allValid) {
      wins.filter(function (w) { return w.valid; }).forEach(function (w) {
        for (var k = 0; k < w.qty; k++) panels.push({ fabW: w.fab.w, fabH: w.fab.h, winW: w.W, winH: w.H, name: w.name, wi: w.i, cn: k });
      });
    }
    var nest = allValid ? this.nest.nestPanels(panels) : null;
    var rollAvail = allValid && !!nest && !nest.oversize && nest.rolls.length > 0;
    var rollMaterial = nest ? nest.rolls.reduce(function (s, ft) { return s + (self.rollPrice(ft) || 0); }, 0) : 0;

    // STUCK-ESCAPE: an oversize build is often just the +1"/side mounting allowance tipping a near-72"
    // window over the 72" roll. If measuring EXACT (perSide 0 — the glass-mount / Swing-out type) would
    // fit, expose that so the Format step can offer a one-tap switch instead of a dead end.
    var exactType = types.find(function (t) { return t.perSide === 0; }) || null;
    var allExact = allValid && validWins.every(function (w) { return w.type && w.type.perSide === 0; });
    var exactFits = false;
    if (allValid && nest && nest.oversize && exactType && !allExact) {
      var exactPanels = [];
      validWins.forEach(function (w) {
        var ef = self.shade.fabricSize(w.W, w.H, 0);
        for (var k = 0; k < w.qty; k++) exactPanels.push({ fabW: ef.w, fabH: ef.h });
      });
      var exactNest = self.nest.nestPanels(exactPanels);
      exactFits = !exactNest.oversize && !!exactNest.rolls && exactNest.rolls.length > 0;
    }
    var rollSnapPacks = this.shade.coverSnaps(totalSnaps);
    var rollSnapCost = rollSnapPacks.reduce(function (s, p) { return s + (self.snaPrice(p.n) || 0) * p.count; }, 0);
    var rollTotal = rollMaterial + rollSnapCost;

    var domePresent = validWins.some(function (w) { return w.dome; });
    var airQty = domePresent
      ? validWins.filter(function (w) { return w.dome; }).reduce(function (s, w) { return s + self.airfloQty(w.W, w.H) * w.qty; }, 0)
      : 0;
    // Body-color studs: size to COVER the snap count at LEAST COST (coverSnaps) — the SAME least-cost cover
    // the roll's SNA snaps use, so the two track each other and the shopper is NEVER sold a strictly-dominated
    // stud combo. (The old coverTight minimized delivered COUNT and was price-blind, so it stacked worst-value
    // 15-packs → overpay AND fewer studs; measured up to ~$10 on a 100-snap build.) studRecommend is computed
    // even when the add-on is OFF so the row can show an accurate "Recommended: N" — the real deliverable count
    // (a whole-pack cover, so N >= snaps; any surplus is usable spares, never the raw geometric count like 24).
    var studRecommend = this.shade.coverSnaps(totalSnaps, this.snsPacks());
    var studCount = studRecommend.reduce(function (s, p) { return s + p.n * p.count; }, 0);
    var studPacks = this.studsActive() ? studRecommend : [];
    var studCost = studPacks.reduce(function (s, p) { return s + (self.snsPrice(p.n) || 0) * p.count; }, 0);
    var bagCost = this.bagQty * (this.acc.storageBag ? this.acc.storageBag.price : 0);
    var airCost = this.airOn ? airQty * (this.acc.airflo ? this.acc.airflo.price : 0) : 0;
    var addOnTotal = studCost + bagCost + airCost;

    var matTotal = this.style === "precut" ? precutTotal : this.style === "roll" ? rollTotal : 0;
    var grand = matTotal + (this.style ? addOnTotal : 0);
    // Free shipping is a USD rate rule, converted at checkout — never promise it to a non-USD shopper.
    var freeShip = isBaseCcy() && grand >= (this.cfg.freeShipOver || 225);

    return {
      wins: wins, validWins: validWins, allValid: allValid, precutAvail: precutAvail,
      precutTotal: precutTotal, totalSnaps: totalSnaps, panels: panels, nest: nest,
      rollAvail: rollAvail, rollTotal: rollTotal, rollSnapPacks: rollSnapPacks,
      exactType: exactType, exactFits: exactFits,
      domePresent: domePresent, airQty: airQty, studPacks: studPacks, studCount: studCount, studCost: studCost,
      bagCost: bagCost, airCost: airCost, addOnTotal: addOnTotal, grand: grand, freeShip: freeShip,
    };
  };

  // ---- catalog price accessors (never a literal) ----
  ShadeFinder.prototype.rollPrice = function (ft) {
    var r = (this.cfg.catalog.rolls || []).find(function (x) { return x.ft === ft; });
    return r ? r.price : null;
  };
  ShadeFinder.prototype.snaPrice = function (n) {
    var p = (this.cfg.catalog.snaPacks || []).find(function (x) { return x.n === n; });
    return p ? p.price : null;
  };
  ShadeFinder.prototype.snsPacks = function () { return this.cfg.catalog.snsPacks || []; };
  // Body-color stud add-on is ROLL-ONLY (owner, 2026-10-07): precut shades are MATCHED ONLY, cap = stud = the
  // mesh color, and their in-box snap pack rides as its own $0 kit line (addToCart). studsOn/studColor stay in
  // state (and the URL) so switching back to roll restores the pick; they just never price or cart on precut.
  ShadeFinder.prototype.studsActive = function () { return !!this.studsOn && this.style !== "precut"; };
  ShadeFinder.prototype.snsPrice = function (n) {
    var p = this.snsPacks().find(function (x) { return x.n === n; });
    return p ? p.price : null;
  };
  ShadeFinder.prototype.airfloQty = function (domeW, domeH) {
    var st = function (d) { return Math.max(0, Math.ceil(d / 12) - 1); };
    return st(domeW) * st(domeH);
  };

  // ---- catalog SKU/variant lookups for the cart ----
  // Resolve the mesh variant by the SELECTED colour (this.color). cfg.catalog.* is FILTERED to the
  // default colour (BK) at Liquid injection, so it alone always yields Black regardless of the shopper's
  // pick — the mesh (roll/precut) and its colour-matched snap caps must come from cfg.catalogAllColors,
  // matched by colour, with the default-colour entry as a fallback if a size lacks the chosen colour.
  // (Price is colour-invariant, so the separate *Price() accessors correctly keep reading cfg.catalog.*.)
  // Mirrors the stud (SNS) remap in buildLines; the off-colour stud add-on stays keyed by studColor.
  ShadeFinder.prototype.precutEntry = function (w, h) {
    var self = this, all = (this.cfg.catalogAllColors && this.cfg.catalogAllColors.precut) || [];
    return all.find(function (x) { return x.w === w && x.h === h && x.color === self.color; })
      || (this.cfg.catalog.precut || []).find(function (x) { return x.w === w && x.h === h; }) || null;
  };
  ShadeFinder.prototype.rollEntry = function (ft) {
    var self = this, all = (this.cfg.catalogAllColors && this.cfg.catalogAllColors.rolls) || [];
    return all.find(function (x) { return x.ft === ft && x.color === self.color; })
      || (this.cfg.catalog.rolls || []).find(function (x) { return x.ft === ft; }) || null;
  };
  ShadeFinder.prototype.snaEntry = function (n) {
    var self = this, all = (this.cfg.catalogAllColors && this.cfg.catalogAllColors.snaPacks) || [];
    return all.find(function (x) { return x.n === n && x.color === self.color; })
      || (this.cfg.catalog.snaPacks || []).find(function (x) { return x.n === n; }) || null;
  };
  ShadeFinder.prototype.snsEntry = function (n) {
    return this.snsPacks().find(function (x) { return x.n === n; }) || null;
  };

  // ---- build cart/order lines (mirrors buildLines) ----
  ShadeFinder.prototype.buildLines = function (d) {
    var self = this, lines = [], cn = this.colorName();
    if (this.style === "precut") {
      d.wins.forEach(function (w) {
        if (!w.match) return;
        var e = self.precutEntry(w.match.w, w.match.h);
        // show the precut oriented to the window (the big dim aligns with the window's big dim) so it never
        // reads smaller than the opening; SKU/variant stay the catalog size. Reinforce "trim to fit".
        var land = w.W >= w.H, big = Math.max(w.match.w, w.match.h), small = Math.min(w.match.w, w.match.h);
        var pw = land ? big : small, ph = land ? small : big;
        var pack = self.shade.precutPackSize(w.match.w, w.match.h);
        lines.push({ variantId: e && e.variantId, sku: e ? e.sku : "",
          title: "Precut Shade: " + pw + "×" + ph + " in, " + cn,
          opts: "covers your " + w.W + "×" + w.H + " window · trim to fit", qty: w.qty, price: (e ? e.price : w.match.price),
          winW: w.W, winH: w.H,
          // The panel's in-box snap pack size (engine precutPackSize). addToCart adds it as its own $0 kit line
          // (EZ-C-SNA-{n}-{mesh}-{mesh}) since the warehouse dropped the pack from the precut BOM (2026-10-06).
          kitPack: pack,
          // Visible "Kit includes" for the precut panel — the snaps/studs ship INSIDE the bundle (hidden in the
          // SKU), so surface them at checkout. Uses the SAME per-panel pack count the builder shows the customer
          // ("Includes N snaps", precutPackSize) and the cart mirrors — so cart == checkout by the same source.
          visProps: { 'Kit includes': pack + ' snaps & studs in the box · trim to fit' } });
      });
    } else if (this.style === "roll" && d.nest) {
      var counts = {};
      d.nest.rolls.forEach(function (ft) { counts[ft] = (counts[ft] || 0) + 1; });
      Object.keys(counts).forEach(function (ftKey) {
        var ft = Number(ftKey), qty = counts[ftKey], r = self.rollEntry(ft);
        lines.push({ variantId: r && r.variantId, sku: r ? r.sku : "",
          title: "Shade Roll: " + ft + " ft, " + cn, opts: "6 ft wide · cut to fit",
          qty: qty, price: r ? r.price : 0 });
      });
      d.rollSnapPacks.forEach(function (p) {
        var e = self.snaEntry(p.n);
        lines.push({ variantId: e && e.variantId, sku: e ? e.sku : "",
          title: "Snap Pack: " + p.n + " ct, " + cn, opts: "caps, pins & studs",
          qty: p.count, price: self.snaPrice(p.n) });
      });
    }
    if (this.studsActive()) { // never on precut: matched-only, no SNS stud lines (studsActive)
      var scName = (this.studHw.find(function (s) { return s.code === self.studColor; }) || {}).name || "";
      // resolve the stud variant by the SELECTED colour — catalog.snsPacks is filtered to the default
      // colour at injection, so snsEntry() alone always yields the default (BK). Match the all-colours
      // catalog by (n + studColor) so an off-colour pick reaches the cart; fall back to the default-colour
      // entry if that size/colour is absent. Price is colour-invariant, so snsPrice() stays correct.
      var allSns = (self.cfg.catalogAllColors && self.cfg.catalogAllColors.snsPacks) || [];
      d.studPacks.forEach(function (p) {
        var e = allSns.find(function (x) { return x.n === p.n && x.color === self.studColor; }) || self.snsEntry(p.n);
        lines.push({ variantId: e && e.variantId, sku: e ? e.sku : "",
          title: "Body-Color Stud Pack: " + p.n + " ct, " + scName, opts: "off-color studs (add-on)",
          qty: p.count, price: self.snsPrice(p.n) });
      });
    }
    if (this.bagQty > 0 && this.acc.storageBag) {
      lines.push({ variantId: this.acc.storageBag.variantId, sku: this.acc.storageBag.sku,
        title: this.acc.storageBag.name, opts: "79 in", qty: this.bagQty, price: this.acc.storageBag.price });
    }
    if (this.airOn && d.airQty > 0 && this.acc.airflo) {
      lines.push({ variantId: this.acc.airflo.variantId, sku: this.acc.airflo.sku,
        title: this.acc.airflo.name, opts: "dome skylight spacer", qty: d.airQty, price: this.acc.airflo.price });
    }
    return lines;
  };

  // ---- multi-line add to Shopify cart, then to /cart ----
  ShadeFinder.prototype.addToCart = function () {
    if (this._atcBusy) return;               // N9: ignore re-entrant clicks (double-tap) while a POST is in flight
    var d = this.derive();
    if (!this.style) return;
    var allLines = this.buildLines(d);
    if (!allLines.length) return;
    var lines = allLines.filter(function (l) { return l.variantId; });
    // N1 cart integrity: never POST a PARTIAL build. If any resolved line lacks a variant (an
    // unpublished / mis-synced product), the shopper was shown a total that INCLUDED it — shipping
    // the rest would give a bare, unbuildable order (e.g. a roll with no snaps) at that price. Block
    // it and surface the problem instead of silently dropping lines.
    if (lines.length !== allLines.length) {
      this.atcError = ATC_PARTIAL_MSG;
      this.setState({});
      return;
    }
    this.atcError = null;
    this._atcBusy = true;
    var self = this;
    // tag every line of this build so the cart can group it into its own section (hidden _ properties)
    var bid = 'b' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    var blabel = ({ rv: 'RV shade', window: 'Home window shade', skylight: 'Skylight shade', boat: 'Boat shade' })[this.app] || 'Exterior shade';
    var bprops = { _build: bid, _build_kind: 'shade', _build_label: blabel };
    // Carry an app-appropriate build image so the cart section shows the RIGHT shot per build (skylight →
    // a skylight image, RV → an RV image, etc.) instead of falling back to the resolved product's featured
    // image, which is RV/window imagery for every app. Per-app source is appMeta[app].img (populated for all
    // four apps in every mode); a directed PDP's gallery hero is the fallback. Absolutize per the cart
    // contract (_build_img must be an ABSOLUTE URL): protocol-relative CDN URLs get https:, root-relative
    // asset paths get the origin; already-absolute http(s) URLs pass through unchanged.
    var bmeta = this.appMeta[this.app] || {};
    var bimg = bmeta.img || (this.cfg.galleryPhotos && this.cfg.galleryPhotos[0]) || "";
    if (bimg) {
      if (bimg.indexOf("//") === 0) bimg = "https:" + bimg;
      else if (bimg.charAt(0) === "/") bimg = ((window.location && window.location.origin) || "") + bimg;
      bprops._build_img = bimg;
    }
    // Save the encoded build so the cart "Edit shades" link deep-links back into the configurator with
    // this exact build restored (hidden _ prop; read by restoreFromUrl via ?app&c&w&fmt&st&bg&af).
    var burl = this.buildQuery();
    if (burl && burl.length > 1) bprops._build_url = burl;
    var items = lines.map(function (l) { return { id: l.variantId, quantity: l.qty || 1, properties: l.visProps ? Object.assign({}, bprops, l.visProps) : bprops }; });
    var root = this.root;
    var post = function (allItems) {
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: allItems }),
      }).then(function (r) {
        if (!r.ok) throw new Error("add failed");
        return r.json();
      }).then(function (data) {
        self._atcBusy = false;
        // Drawer by default; hard /cart redirect only when the section opts out.
        if (root && root.getAttribute("data-cart-redirect") === "true") {
          window.location.href = (window.routes && window.routes.cart_url) || "/cart";
          return;
        }
        document.dispatchEvent(new CustomEvent("ez:cart:added", { detail: { cart: data } }));
      }).catch(function () {
        self._atcBusy = false;
        window.location.href = "/cart";
      });
    };
    // Precut: resolve every panel's in-box snap pack line BEFORE posting. All-or-nothing: if the helper is
    // missing or any pack variant can't be resolved, post NOTHING (a precut without its snaps is unbuildable)
    // and surface the same partial-order error as N1 above. Roll builds resolve to `items` unchanged.
    this.withKitSnaps(lines, items).then(post, function (err) {
      if (window.console) console.warn("[ez-shade] kit snaps unavailable, nothing added", err);
      self._atcBusy = false;
      self.atcError = ATC_PARTIAL_MSG;
      self.setState({});
    });
  };

  // Precut kit snaps: after EACH precut panel item, insert its in-box snap pack (EZKitSnaps two-code
  // EZ-C-SNA-{kitPack}-{mesh}-{mesh}, cap = stud = mesh color: precuts are matched-only). Quantity scales by the
  // panel line's qty (kitQty), and the snap line carries ONLY that panel's underscore grouping props (_build,
  // _build_kind, _build_label, _build_img, _build_url), never the visible "Kit includes"; the helper adds
  // `_kit_snaps: "1"` so the cart transform prices it $0 inside the same _build. Non-precut styles pass through.
  ShadeFinder.prototype.withKitSnaps = function (lines, items) {
    if (this.style !== "precut") return Promise.resolve(items);
    var K = window.EZKitSnaps;
    if (!K || typeof K.lines !== "function") return Promise.reject(new Error("EZKitSnaps helper not loaded"));
    var mesh = this.color;
    return Promise.all(items.map(function (it, i) {
      var l = lines[i];
      if (!l || !l.kitPack) return [];
      var grp = {}, src = it.properties || {};
      Object.keys(src).forEach(function (k) { if (k.charAt(0) === "_") grp[k] = src[k]; });
      return Promise.resolve().then(function () {
        return K.lines({ packs: [{ n: l.kitPack, qty: 1 }], cap: mesh, stud: mesh, kitQty: it.quantity, props: grp });
      }).then(function (snap) {
        if (!snap || !snap.length) throw new Error("kit snaps: no pack resolved for " + l.sku);
        return snap;
      });
    })).then(function (snapSets) {
      var out = [];
      items.forEach(function (it, i) { out.push(it); Array.prototype.push.apply(out, snapSets[i]); });
      return out;
    });
  };

  // ---- persistence ----
  ShadeFinder.prototype.storeKey = function () { return "ezsnap_shade_" + this.app; };
  ShadeFinder.prototype.persist = function () {
    if (!this.app) return;
    try {
      localStorage.setItem(this.storeKey(), JSON.stringify({
        color: this.color, windows: this.windows, style: this.style, studsOn: this.studsOn,
        studColor: this.studColor, bagQty: this.bagQty, airOn: this.airOn, open: this.open,
      }));
      localStorage.setItem("ezsnap_shade_last", this.app);
    } catch (e) {}
    this.syncUrl();
  };
  ShadeFinder.prototype.restore = function () {
    try {
      var saved = JSON.parse(localStorage.getItem(this.storeKey()) || "null");
      if (saved) {
        this.color = saved.color || this.color;
        this.windows = (saved.windows && saved.windows.length) ? saved.windows : [];
        this.style = saved.style || null;
        this.studsOn = !!saved.studsOn; this.studColor = saved.studColor || "BK";
        this.bagQty = saved.bagQty || 0; this.airOn = !!saved.airOn;
        this.open = saved.open || 2;
      } else { this.windows = []; }
    } catch (e) { this.windows = []; }
  };

  // ---- URL persistence (mirror the build to the address bar; shareable / emailable link) ----
  // Encoding: ?app=<id>&c=<color>&w=<WxHxQTYxTYPE * …>&fmt=p|r&st=<studColor>&bg=<n>&af=1
  //   windows join on "*"; fields join on "x" (both survive URLSearchParams unescaped and never
  //   collide with the numeric W/H/QTY or the slug type ids). Same read-on-load + write-on-change
  //   contract the skirting + hitch configurators use, so all three save state to the URL.
  ShadeFinder.prototype.hasUrlState = function () {
    var sp = new URLSearchParams(window.location.search);
    // w / fmt are unambiguous build markers. "app" alone only restores in master mode (directed is
    // route-locked and ignores the URL app), so it must not suppress the localStorage fallback there.
    if (sp.has("w") || sp.has("fmt")) return true;
    return !this.directed && sp.has("app");
  };
  ShadeFinder.prototype.encodeWins = function () {
    return this.windows.map(function (x) {
      return [x.w, x.h, x.qty, x.type].join("x");
    }).join("*");
  };
  // The build encoded as a "?app&c&w&fmt&st&bg&af" query — the single source for both the address-bar
  // mirror (syncUrl) and the cart line's saved _build_url (so "Edit shades" reopens the exact build).
  ShadeFinder.prototype.buildQuery = function () {
    if (!this.app) return "";
    var p = new URLSearchParams();
    p.set("app", this.app);
    if (this.color) p.set("c", this.color);
    if (this.windows.length) p.set("w", this.encodeWins());
    if (this.style) p.set("fmt", this.style === "precut" ? "p" : "r");
    if (this.studsOn) p.set("st", this.studColor);
    if (this.bagQty > 0) p.set("bg", String(this.bagQty));
    if (this.airOn) p.set("af", "1");
    return "?" + p.toString();
  };
  ShadeFinder.prototype.syncUrl = function () {
    if (!this.app) return;
    try {
      // Only mirror once there's a real build to share (a window added or a format chosen); before
      // that, keep the address bar clean (a bare directed PDP / freshly-picked app writes nothing).
      var meaningful = this.windows.length > 0 || !!this.style;
      var target;
      if (!meaningful) {
        target = window.location.pathname;
      } else {
        target = window.location.pathname + this.buildQuery();
      }
      // Idempotent: skip the write when the URL is already correct. persist() is reached twice per
      // state change (setState + render both call it), and doubled replaceState on rapid typing can
      // trip WebKit's ~100-calls/30s history cap on iOS — de-duping keeps the URL reliably mirrored.
      if (target === window.location.pathname + window.location.search) return;
      window.history.replaceState(null, "", target);
    } catch (e) {}
  };
  ShadeFinder.prototype.restoreFromUrl = function () {
    var sp = new URLSearchParams(window.location.search);
    // app: master mode reads it from the URL; directed mode is route-locked (ignore the value).
    if (!this.directed) {
      var app = sp.get("app");
      if (app && this.apps.some(function (a) { return a.id === app; })) this.app = app;
    }
    if (!this.app) return false;
    var ao = this.appObj(), types = (ao && ao.types) || [];
    var typeOk = function (id) { return types.some(function (t) { return t.id === id; }); };
    var c = sp.get("c");
    if (c && this.colors.some(function (x) { return x.code === c; })) this.color = c;
    var w = sp.get("w");
    if (w) {
      this.windows = w.split("*").map(function (chunk) {
        var f = chunk.split("x");
        var ty = f.slice(3).join("x");
        if (!typeOk(ty)) ty = (types[0] && types[0].id) || ty;
        return { w: f[0] || "", h: f[1] || "", qty: f[2] || "1", type: ty };
      });
    }
    var stc = sp.get("st");
    if (stc && this.studHw.some(function (s) { return s.code === stc; })) { this.studsOn = true; this.studColor = stc; }
    var bg = sp.get("bg"); if (bg) this.bagQty = Math.max(0, parseInt(bg, 10) || 0);
    if (sp.get("af") === "1") this.airOn = true;
    // format: only honour it if that format is actually available for the restored windows; then
    // derive which accordion step opens (mirror skirting/hitch restore landing on the review step).
    var fmt = sp.get("fmt");
    var d = (this.shade && this.nest) ? this.derive() : null;
    if (d) {
      if (fmt === "p" && d.precutAvail) this.style = "precut";
      else if (fmt === "r" && d.rollAvail) this.style = "roll";
      this.open = this.style ? 6 : d.allValid ? 4 : (this.windows.length ? 3 : 2);
    } else {
      if (fmt === "p") this.style = "precut"; else if (fmt === "r") this.style = "roll";
      this.open = this.style ? 6 : (this.windows.length ? 3 : 2);
    }
    return true;
  };

  // ---- app selection ----
  ShadeFinder.prototype.scrollToBuilder = function (delay) {
    var self = this;
    setTimeout(function () {
      var f = self.root.querySelector(".ezsf__builder");
      if (f && f.scrollIntoView) f.scrollIntoView({ behavior: "smooth", block: "start" });
    }, delay || 60);
  };
  ShadeFinder.prototype.startApp = function (id) {
    this.app = id;
    this.activePhoto = 0;
    this.restore();
    this.render();
    this.scrollToBuilder(60);
  };
  ShadeFinder.prototype.changeApp = function () {
    this.app = null;
    try { window.history.replaceState(null, "", window.location.pathname); } catch (e) {}
    this.render();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---- window edits ----
  ShadeFinder.prototype.setWin = function (idx, patch, clearStyle) {
    this.windows = this.windows.map(function (x, i) { return i === idx ? Object.assign({}, x, patch) : x; });
    if (clearStyle) this.style = null;
  };
  ShadeFinder.prototype.addWindow = function () {
    var prev = this.windows[this.windows.length - 1];
    var ao = this.appObj();
    this.windows = this.windows.concat([this.newWin(prev ? prev.type : ao.types[0].id)]);
    this.winOpen = this.windows.length - 1;
    this.style = null;
  };
  ShadeFinder.prototype.removeWindow = function (idx) {
    this.windows = this.windows.filter(function (_, i) { return i !== idx; });
    this.winOpen = this.winOpen === idx ? -1 : (this.winOpen > idx ? this.winOpen - 1 : this.winOpen);
    this.style = null;
  };

  // =========================================================================
  // RENDER — full re-render on any state change (island is small; state is simple).
  // =========================================================================
  ShadeFinder.prototype.setState = function (patch) {
    Object.assign(this, patch);
    this.persist();
    this.render();
  };

  ShadeFinder.prototype.render = function () {
    this.root.innerHTML = "";
    if (!this.directed) this.root.appendChild(this.renderAppPicker());
    if (this.app) {
      this.persist();
      this.root.appendChild(this.renderBuilder());
    }
  };

  // ---- STEP 1: application picker (dark showcase) ----
  ShadeFinder.prototype.renderAppPicker = function () {
    var self = this, c = this.copy;
    var sec = el("section", "ezsf__pick" + (this.app ? " ezsf__pick--chosen" : ""));
    var inner = el("div", "ezsf__pick-inner");

    if (!this.app) {
      var head = el("div", "ezsf__pick-head");
      head.appendChild(el("div", "ezsf__pick-eyebrow", esc(c.pick_eyebrow || "EXTERIOR WINDOW SHADES · ONE MESH, ANY WINDOW")));
      var pickH = esc(c.pick_heading || "Find your exterior window shades.").replace(/\.$/, '<span class="ezsf__pick-dot">.</span>');
      head.appendChild(el("h2", "ezsf__pick-h", pickH));
      head.appendChild(el("p", "ezsf__pick-lede", esc(c.pick_lede || "One mesh and one no-drill 3M™ VHB snap fit every exterior window: RV, home, skylight or boat. Tell us your windows and we size them both ways: ready-made precuts with the snaps included, or a roll you cut to fit.")));
      inner.appendChild(head);
      var q = el("div", "ezsf__pick-q");
      q.appendChild(el("span", "ezsf__pick-qh", esc(c.pick_question || "Where are you shading?")));
      q.appendChild(el("span", "ezsf__pick-qs", esc(c.pick_question_sub || "Same shade either way: this just tailors the window types and measuring tips to your install.")));
      inner.appendChild(q);
    }

    var grid = el("div", "ezsf__apps");
    this.apps.forEach(function (a) {
      var m = self.appMeta[a.id] || {};
      var card = el("button", "ezsf__app" + (self.app === a.id ? " is-sel" : ""));
      card.type = "button";
      card.setAttribute("data-app", a.id);
      var media = el("span", "ezsf__app-media");
      if (m.img) media.style.backgroundImage = "url('" + m.img + "')";
      card.appendChild(media);
      card.appendChild(el("span", "ezsf__app-scrim"));
      var lab = el("span", "ezsf__app-label");
      if (m.stat) lab.appendChild(el("span", "ezsf__app-stat", esc(m.stat)));
      lab.appendChild(el("span", "ezsf__app-title", esc(a.label)));
      if (m.pitch) lab.appendChild(el("span", "ezsf__app-pitch", esc(m.pitch)));
      card.appendChild(lab);
      card.addEventListener("click", function () { self.startApp(a.id); });
      grid.appendChild(card);
    });
    inner.appendChild(grid);
    sec.appendChild(inner);
    return sec;
  };

  // ---- accordion section shell (peek preview like the demo) ----
  ShadeFinder.prototype.stepShell = function (n, title, opts) {
    opts = opts || {};
    var self = this;
    var isOpen = this.open === n;
    var isPeek = !isOpen && n === this.open + 1;
    var locked = !!opts.locked;
    var done = !!opts.done;

    var wrap = el("div", "ezsf__step" + (opts.last ? " ezsf__step--last" : "") + (locked && !isPeek ? " is-locked" : ""));
    wrap.setAttribute("data-step", String(n));
    var headBtn = el("button", "ezsf__step-head");
    headBtn.type = "button";
    var num = el("span", "ezsf__step-num" + (done ? " is-done" : isOpen ? " is-open" : ""), done ? "✓" : String(n));
    headBtn.appendChild(num);
    var t = el("span", "ezsf__step-title", esc(title) + (opts.badge != null ? ' <span class="ezsf__step-badge">' + esc(opts.badge) + "</span>" : ""));
    headBtn.appendChild(t);
    if (!isOpen && !opts.expand && !locked && done && opts.chip != null) {
      headBtn.appendChild(el("span", "ezsf__step-chip", esc(opts.chip) + ' <span class="ezsf__edit">edit</span>'));
    }
    if (locked) headBtn.appendChild(el("span", "ezsf__step-lock", "🔒"));
    if (!locked && !isOpen) headBtn.addEventListener("click", function () { self.setState({ open: n }); });
    wrap.appendChild(headBtn);

    if ((isOpen || opts.expand) && !locked) {
      var body = el("div", "ezsf__step-body");
      if (opts.build) opts.build(body);
      wrap.appendChild(body);
    } else if (isPeek) {
      var peek = el("div", "ezsf__peek");
      var ghost = el("div", "ezsf__peek-ghost");
      ghost.setAttribute("aria-hidden", "true");
      if (opts.build) opts.build(ghost);
      peek.appendChild(ghost);
      // No floating CTA on the ghost — the visible advance button now lives at the bottom of the ACTIVE
      // step. The ghost stays click-to-advance via an invisible overlay (suppressed when the step is locked).
      if (!locked) {
        var hit = el("button", "ezsf__peek-hit");
        hit.type = "button";
        hit.setAttribute("aria-label", "Continue to " + esc(title));
        hit.addEventListener("click", function () { self.setState({ open: n }); });
        peek.appendChild(hit);
      }
      wrap.appendChild(peek);
    }
    return wrap;
  };

  // Visible copper advance button at the bottom of an active step (color, format) — replaces the CTA that
  // used to float on the ghosted next step. Large + high-contrast for older customers; disabled until the
  // step's pick is made.
  ShadeFinder.prototype.stepContinue = function (label, target, disabled) {
    var self = this;
    var btn = el("button", "ezsf__stepcont", esc(label));
    btn.type = "button";
    btn.disabled = !!disabled;
    if (!disabled) btn.addEventListener("click", function () { self.setState({ open: target }); });
    return btn;
  };

  // directed product header (title / rating / from-price / value-adds / ship line) — the demo
  // ShadeConfigurator `directed && header` block. from-price is the MIN precut in the catalog
  // (sourced from the live products; never typed).
  ShadeFinder.prototype.renderHeader = function (extraClass) {
    var h = this.header || {};
    var wrap = el("div", "ezsf__phead" + (extraClass ? " " + extraClass : ""));
    wrap.appendChild(el("div", "ezsf__phead-title", esc(h.title || "")));
    if (h.ratingLabel) {
      var parts = String(h.ratingLabel).split("★");
      var rate = el("div", "ezsf__phead-rating");
      rate.innerHTML = "<b>" + esc(parts[0]) + '</b><span class="ezsf__phead-star">★</span>' + esc(parts.slice(1).join("★"));
      wrap.appendChild(rate);
    }
    var precut = (this.cfg.catalog && this.cfg.catalog.precut) || [];
    var fromPrice = precut.length ? Math.min.apply(null, precut.map(function (p) { return p.price; })) : 0;
    var fromRow = el("div", "ezsf__phead-from");
    fromRow.appendChild(el("span", "ezsf__phead-from-lab", "from"));
    fromRow.appendChild(el("span", "ezsf__phead-from-val", money(fromPrice)));
    wrap.appendChild(fromRow);
    var adds = h.valueAdds || [];
    if (adds.length) {
      var va = el("div", "ezsf__phead-adds");
      adds.forEach(function (v) {
        var row = el("div", "ezsf__phead-add");
        row.appendChild(el("span", "ezsf__phead-dot"));
        row.appendChild(el("span", "ezsf__phead-addt", esc(v)));
        va.appendChild(row);
      });
      wrap.appendChild(va);
    }
    wrap.appendChild(el("div", "ezsf__phead-ship", "Free shipping " + fsLabel(this.cfg.freeShipOver || 225) + "+ · Limited Lifetime Warranty"));
    return wrap;
  };

  // per-application PDP href for the directed switcher (mapped from config; falls back to the master builder)
  ShadeFinder.prototype.appHref = function (id) {
    var links = this.cfg.appLinks || {};
    return links[id] || (this.cfg.masterLink || "/pages/exterior-window-shade-kit");
  };

  // ---- the builder (product header + accordion + gallery rail) ----
  ShadeFinder.prototype.renderBuilder = function () {
    var self = this, d = this.derive();
    var ao = this.appObj(), meta = this.appMeta[this.app] || {};
    var sec = el("section", "ezsf__builder" + (this.directed ? " ezsf__builder--directed" : ""));
    var inner = el("div", "ezsf__builder-inner");

    // top bar: MASTER builder only = product identity + change-app. In directed (per-app PDP) mode
    // the back-link + app-switcher now live in the breadcrumb row (ez-breadcrumb, server-rendered,
    // on the SAME line as the crumbs), so the island renders no top bar here.
    if (!this.directed) {
      var bar = el("div", "ezsf__bar");
      var ident = el("div", "ezsf__ident");
      var sw = el("span", "ezsf__ident-thumb");
      if (meta.img) sw.style.backgroundImage = "url('" + meta.img + "')";
      ident.appendChild(sw);
      var idt = el("div");
      idt.appendChild(el("div", "ezsf__ident-name", esc("EZ-Snap " + (meta.label || ao.label))));
      var summ = [this.colorName(), this.style === "precut" ? "Precut" : this.style === "roll" ? "Roll" : null].filter(Boolean).join(" · ") || (meta.stat || "");
      idt.appendChild(el("div", "ezsf__ident-sub", esc(summ)));
      ident.appendChild(idt);
      bar.appendChild(ident);
      var change = el("button", "ezsf__change", "Change application ←");
      change.type = "button";
      change.addEventListener("click", function () { self.changeApp(); });
      bar.appendChild(change);
      inner.appendChild(bar);
    }

    var grid = el("div", "ezsf__grid" + (this.directed ? " ezsf__grid--directed" : ""));
    var col = el("div", "ezsf__col");
    if (this.directed && this.header) col.appendChild(this.renderHeader());
    var card = el("div", "ezsf__card");
    card.appendChild(el("div", "ezsf__card-tab"));

    // STEP 2 — colour
    card.appendChild(this.stepShell(2, "Mesh color", {
      done: this.open > 2, chip: this.open > 2 ? this.colorName() : null,
      build: function (b) { self.buildColor(b); },
    }));
    // STEP 3 — windows/skylights. Count = SUM of per-row quantities (a row with QTY 2 is two windows),
    // not the number of rows — the engine already sizes by qty; this makes the display agree.
    var winCount = d.validWins.reduce(function (s, w) { return s + w.qty; }, 0);
    card.appendChild(this.stepShell(3, this.winNoun(true), {
      locked: !this.color, done: this.open > 3 && d.allValid,
      // Windows + Format are directly linked (you pick a format FOR these), so keep it expanded
      // while Format is the active step instead of collapsing it to a chip.
      expand: this.open === 4,
      badge: winCount ? winCount + " added" : null,
      chip: this.open > 3 ? (winCount + " " + self.winNoun(winCount === 1 ? false : true).toLowerCase()) : null,
      build: function (b) { self.buildWindows(b, d); },
    }));
    // STEP 4 — format
    card.appendChild(this.stepShell(4, "Choose your format", {
      locked: !d.allValid, done: this.open > 4 && !!this.style,
      chip: this.open > 4 && this.style ? (this.style === "precut" ? "Precuts" : "Roll") : null,
      build: function (b) { self.buildFormat(b, d); },
    }));
    // STEP 5 — add-ons
    card.appendChild(this.stepShell(5, "Add-ons", {
      locked: !this.style, done: this.open > 5,
      chip: this.open > 5 ? (d.addOnTotal ? money(d.addOnTotal) : "None") : null,
      build: function (b) { self.buildAddons(b, d); },
    }));
    // STEP 6 — order
    card.appendChild(this.stepShell(6, "Your order", {
      locked: !this.style, last: true,
      build: function (b) { self.buildOrder(b, d); },
    }));

    col.appendChild(card);
    // mobile: the product identity leads (title + rating + from-price above the gallery), matching the
    // skirting builder + standard PDPs. This grid-level copy of the header shows ONLY on mobile (see
    // .ezsf__phead--mobiletop); desktop keeps the in-column header untouched. The header is static
    // content (no handlers/ids), so the duplicate node is inert.
    if (this.directed && this.header) grid.appendChild(this.renderHeader("ezsf__phead--mobiletop"));
    grid.appendChild(col);
    grid.appendChild(this.renderGallery(d, meta));
    inner.appendChild(grid);
    sec.appendChild(inner);
    return sec;
  };

  // ---- STEP 2 body ----
  ShadeFinder.prototype.buildColor = function (b) {
    var self = this;
    b.appendChild(el("p", "ezsf__note", "Your mesh, snap caps, and studs all ship in the color you pick."));
    var grid = el("div", "ezsf__color-grid");
    this.colors.forEach(function (c) {
      var opt = el("button", "ezsf__color" + (self.color === c.code ? " is-sel" : ""));
      opt.type = "button";
      if (c.popular) opt.appendChild(el("span", "ezsf__color-pop", "MOST POPULAR"));
      var sw = el("span", "ezsf__color-sw ezsf__color-sw--" + esc(c.code.toLowerCase()));
      opt.appendChild(sw);
      var txt = el("span", "ezsf__color-txt");
      txt.appendChild(el("span", "ezsf__color-name", esc(c.name)));
      txt.appendChild(el("span", "ezsf__color-sub", esc(c.code === "BK" ? "Up to 90% heat blocked" : "More light, open view")));
      opt.appendChild(txt);
      opt.addEventListener("click", function () { if (self.color !== c.code) self._animColor = true; self.setState({ color: c.code }); });
      grid.appendChild(opt);
    });
    b.appendChild(grid);
    // A2: post-selection confirmation — reveals what the colour choice includes. Animates only on an active
    // pick (_animColor); static when a colour is already selected on load (restore / default).
    if (this.color) {
      var picked = this.colors.find(function (c) { return c.code === self.color; });
      var cname = picked ? picked.name : this.color;
      var confirm = el("div", "ezsf__color-confirm" + (this._animColor ? " is-animating" : ""));
      confirm.appendChild(el("span", "ezsf__color-confirm-mark", "✓"));
      confirm.appendChild(el("span", "ezsf__color-confirm-txt",
        "You picked " + esc(cname) + ". Mesh, snap caps, and studs all ship in " + esc(cname) + ", so the fasteners blend into the screen."));
      b.appendChild(confirm);
      this._animColor = false;
    }
    b.appendChild(this.stepContinue("Continue to " + this.winNoun(true) + " →", 3, !this.color));
  };

  // App-aware noun for the window/skylight step. Skylight products read "Skylight(s)"; every other
  // application returns "Window(s)" — i.e. identical to the previous hardcoded copy, so non-skylight
  // builders are unchanged.
  ShadeFinder.prototype.winNoun = function (plural) {
    var sk = this.app === "skylight";
    return sk ? (plural ? "Skylights" : "Skylight") : (plural ? "Windows" : "Window");
  };

  // ---- STEP 3 body ----
  ShadeFinder.prototype.buildWindows = function (b, d) {
    var self = this, ao = this.appObj();
    var itemWord = ao.id === "skylight" ? "skylight" : "window";
    b.appendChild(el("p", "ezsf__note", "Add each " + esc(itemWord) + " you want to shade and enter its size: we size the fabric (with the right mounting allowance for the type) and the snaps for you. Measure the glass opening; the shade trims to fit."));
    if (this.windows.length) b.appendChild(el("div", "ezsf__winlead", "Your " + self.winNoun(true).toLowerCase()));

    this.windows.forEach(function (win, idx) {
      var g = d.wins[idx], isOpenPane = self.winOpen === idx, type = g && g.type;
      var pane = el("div", "ezsf__win" + (isOpenPane ? " is-open" : ""));
      var wh = el("button", "ezsf__win-head");
      wh.type = "button";
      wh.appendChild(el("span", "ezsf__win-name",
        (g && g.valid && !isOpenPane) ? (g.W + "″ × " + g.H + "″ · " + esc(type.label) + " ×" + g.qty) : (self.winNoun(false) + " " + (idx + 1))));
      var ctrl = el("span", "ezsf__win-ctrl");
      if (self.windows.length > 1) {
        var rm = el("span", "ezsf__win-rm", "✕");
        rm.addEventListener("click", function (e) { e.stopPropagation(); self.removeWindow(idx); self.render(); self.persist(); });
        ctrl.appendChild(rm);
      }
      ctrl.appendChild(el("span", "ezsf__win-caret", isOpenPane ? "▴" : "▾"));
      wh.appendChild(ctrl);
      wh.addEventListener("click", function () { self.winOpen = isOpenPane ? -1 : idx; self.render(); });
      pane.appendChild(wh);

      if (isOpenPane) {
        var body = el("div", "ezsf__win-body");
        // B3: pick the window TYPE first (it sets the mounting allowance + how to measure), then the size.
        // App-aware noun so the skylight app reads "Skylight type"; every other app stays "Window type".
        body.appendChild(el("div", "ezsf__win-sublead", self.winNoun(false) + " type"));
        var typeGrid = el("div", "ezsf__wtype-grid");
        typeGrid.style.gridTemplateColumns = "repeat(" + ao.types.length + ",1fr)";
        var thumbs = self.wtypeThumb[self.app] || {};
        ao.types.forEach(function (t) {
          var tc = el("button", "ezsf__wtype" + (win.type === t.id ? " is-sel" : ""));
          tc.type = "button";
          if (thumbs[t.id]) { var th = el("span", "ezsf__wtype-thumb"); th.style.backgroundImage = "url('" + thumbs[t.id] + "')"; tc.appendChild(th); }
          tc.appendChild(el("span", "ezsf__wtype-lab", esc(t.label)));
          tc.appendChild(el("span", "ezsf__wtype-side", t.perSide === 0 ? "exact size" : "+" + t.perSide + "″/side"));
          tc.addEventListener("click", function () { self.setWin(idx, { type: t.id }, true); self.render(); self.persist(); });
          typeGrid.appendChild(tc);
        });
        body.appendChild(typeGrid);

        body.appendChild(el("div", "ezsf__win-sublead", "Size (measure the glass opening)"));
        var fields = el("div", "ezsf__win-fields");
        [{ k: "w", lab: "Width", ph: "e.g. 20", unit: "in" }, { k: "h", lab: "Height", ph: "e.g. 32", unit: "in" }, { k: "qty", lab: "Qty", ph: "1", center: true }].forEach(function (f) {
          var lab = el("label", "ezsf__field" + (f.center ? " ezsf__field--c" : ""));
          lab.appendChild(el("span", "ezsf__field-lab", esc(f.lab)));
          var inp = el("input", "ezsf__input" + (f.unit ? " ezsf__input--unit" : ""));
          inp.type = "number"; inp.placeholder = f.ph; inp.value = win[f.k]; inp.setAttribute("inputmode", "numeric");
          inp.addEventListener("input", function (e) {
            self.setWin(idx, (function () { var o = {}; o[f.k] = e.target.value; return o; })(), f.k !== "qty");
            self.rerenderStep3();
          });
          if (f.unit) {
            // unit chip (in) inside the field — the unit moves out of the label into the chip
            var wrap = el("div", "ezsf__field-wrap");
            wrap.appendChild(inp);
            var u = el("span", "ezsf__unit", '<span class="ezsf__chk">✓</span>' + esc(f.unit)); u.setAttribute("aria-hidden", "true");
            wrap.appendChild(u);
            lab.appendChild(wrap);
          } else {
            lab.appendChild(inp);
          }
          fields.appendChild(lab);
        });
        body.appendChild(fields);

        // Live readout (sizing math + measure tip) lives in its own wrapper so it can be
        // rebuilt in place on each keystroke WITHOUT replacing the dimension inputs above it.
        body.appendChild(self.buildWinDyn(g));
        pane.appendChild(body);
      }
      b.appendChild(pane);
    });

    var addWrap = el("div", "ezsf__addwrap");
    var add = el("button", "ezsf__addwin" + (this.windows.length === 0 ? " is-first" : ""),
      "+ Add " + (this.windows.length === 0 ? "your first" : "another") + " " + esc(itemWord));
    add.type = "button";
    add.addEventListener("click", function () { self.addWindow(); self.render(); self.persist(); });
    addWrap.appendChild(add);
    b.appendChild(addWrap);

    // The advance button is only meaningful while Windows is the active step. When Format is already open
    // (Windows kept expanded beside it), it would be a redundant "Continue to a step you're on", so skip it.
    if (self.open < 4) {
      var cont = el("button", "ezsf__continue" + (d.allValid ? " is-on" : ""),
        d.allValid ? "Continue →" : "Enter a " + itemWord + " to continue");
      cont.type = "button";
      cont.disabled = !d.allValid;
      cont.addEventListener("click", function () {
        self.windows = (function () {
          var keep = self.windows.filter(function (x) { return Number(x.w) > 0 && Number(x.h) > 0; });
          return keep.length ? keep : self.windows;
        })();
        self.winOpen = -1;
        self.setState({ open: 4 });
      });
      b.appendChild(cont);
    }
  };

  // Build one window's live readout (sizing math + measure tip) as a self-contained wrapper.
  // Kept separate from the fields so rerenderStep3 can swap it without touching the inputs.
  ShadeFinder.prototype.buildWinDyn = function (g) {
    var wrap = el("div", "ezsf__win-dyn");
    var type = g && g.type;
    // App-aware noun for the sizing readout (skylight app -> "skylight", every other app -> "window", unchanged).
    var itemWord = this.app === "skylight" ? "skylight" : "window";
    if (g && g.valid && g.fab) {
      var perSide = (g.type && typeof g.type.perSide === "number") ? g.type.perSide : 1;
      var exact = perSide === 0;
      var edu = el("div", "ezsf__sizing");
      // PROMINENT, format-neutral reassurance — answers "why so much fabric for my little window?". The
      // precut-panel vs roll ship size is an equal choice that comes at the format step, so we do not
      // commit to either here; we only explain the coverage the window needs and that it trims to fit.
      if (exact) {
        edu.appendChild(el("div", "ezsf__sizing-lead", "Sized to your <b>" + g.W + "×" + g.H + " in</b> opening."));
        edu.appendChild(el("div", "ezsf__sizing-trim", "This type mounts on the glass, so your shade matches the " + itemWord + "."));
      } else {
        edu.appendChild(el("div", "ezsf__sizing-lead", "Your " + g.W + "×" + g.H + " " + itemWord + " needs <b>" + g.fab.w + "×" + g.fab.h + " in</b> of coverage."));
        edu.appendChild(el("div", "ezsf__sizing-trim", "You will trim it to fit."));
      }
      // MUTED details below the primary line: the frame-mount "why", per-panel fastener facts, the roll
      // note when no precut fits, and how to measure. (B4: kept, but out of the primary flow.)
      var det = el("div", "ezsf__sizing-det");
      if (!exact) {
        det.appendChild(el("div", "ezsf__sizing-detrow", "The no-drill snaps bond around the frame, not on the glass, so the shade reaches " + perSide + "″ past each side."));
      }
      var snaps = this.shade.precutSnaps(g.fab.w, g.fab.h);
      var spacing = (g.fab.w / 12) * (g.fab.h / 12) > 24 ? 5 : 10;
      det.appendChild(el("div", "ezsf__sizing-detrow", "<b>" + snaps + " snaps</b> per panel, " + spacing + "″ max spacing."));
      if (!g.match) {
        det.appendChild(el("div", "ezsf__sizing-detrow ezsf__sizing-detrow--roll", "Larger than our ready-made sizes, so it ships as a 6 ft roll you cut to fit."));
      }
      if (type) {
        det.appendChild(el("div", "ezsf__sizing-detrow", "<b>How to measure:</b> " + esc(type.tip) + ' <a class="ezsf__link" href="' + esc(this.links.help || "/pages/help") + '">Detailed guide →</a>'));
      }
      edu.appendChild(det);
      wrap.appendChild(edu);
    }
    return wrap;
  };

  // Update step 3's derived readout as the user types, WITHOUT rebuilding the dimension inputs.
  // A full render replaces the <input> the user is in — and because type="number" exposes no
  // selectionStart, focus could not be restored, so the field blurred after one digit (the bug).
  // Here we only swap the open window's readout wrapper and refresh the Continue button; the
  // inputs are never touched, so native focus + caret stay put. Typing is seamless.
  ShadeFinder.prototype.rerenderStep3 = function () {
    this.persist();
    var d = this.derive();
    var idx = this.winOpen;
    if (idx > -1) {
      var pane = this.root.querySelector(".ezsf__win.is-open");
      if (pane) {
        var newDyn = this.buildWinDyn(d.wins[idx]);
        var oldDyn = pane.querySelector(".ezsf__win-dyn");
        if (oldDyn) oldDyn.parentNode.replaceChild(newDyn, oldDyn);
        else { var body = pane.querySelector(".ezsf__win-body"); if (body) body.appendChild(newDyn); }
      }
    }
    var cont = this.root.querySelector(".ezsf__continue");
    if (cont) {
      cont.disabled = !d.allValid;
      cont.className = "ezsf__continue" + (d.allValid ? " is-on" : "");
      cont.textContent = d.allValid ? "Continue →" : "Enter a window to continue";
    }
    // B5: keep the right-rail gallery live as dims land, so the Build view appears the moment the window
    // is valid (not only on the next full render). Safe here — renderGallery never touches the focused
    // window inputs, so caret/focus stay put, unlike a full render (which is why this method exists).
    var gal = this.root.querySelector(".ezsf__gallery");
    if (gal) {
      var meta = this.appMeta[this.app] || {};
      gal.parentNode.replaceChild(this.renderGallery(d, meta), gal);
    }
    // With Windows kept expanded beside Format (open===4), a live dim edit must also refresh the Format
    // cards so their price + ship size track the new dims. rerenderStep3 skips the full render to keep the
    // window input's focus/caret, so re-render just the Format body in place here.
    if (this.open === 4) {
      var fmtBody = this.root.querySelector('[data-step="4"] > .ezsf__step-body');
      if (fmtBody) { fmtBody.innerHTML = ""; this.buildFormat(fmtBody, d); }
    }
  };

  // ---- STEP 4 body ----
  ShadeFinder.prototype.buildFormat = function (b, d) {
    var self = this;
    b.appendChild(el("p", "ezsf__note",
      "Both totals cover your " + (d.panels.length === 0 ? "panels" : d.panels.length + " panel" + (d.panels.length === 1 ? "" : "s")) + ". One format for the whole order; add-ons come next."));

    // FIX 2 — un-stick an allowance-induced oversize: if measuring to the glass (exact / Swing-out) fits,
    // offer a one-tap switch up top instead of leaving BOTH format cards disabled with no path forward.
    if (!d.rollAvail && !d.precutAvail && d.exactFits && d.exactType) {
      var esc = el("div", "ezsf__fmt-escape");
      esc.appendChild(el("div", "ezsf__fmt-escape-h", "Too large with the mounting allowance"));
      esc.appendChild(el("div", "ezsf__fmt-escape-p",
        "The +1″/side we add for frame-mounting pushes this past the 72″ roll. If these are swing-out / awning windows you measure to the glass, they fit at exact size."));
      var escBtn = el("button", "ezsf__fmt-escape-btn", "Switch to " + d.exactType.label + " · exact size →");
      escBtn.type = "button";
      escBtn.addEventListener("click", function () {
        (self.windows || []).forEach(function (w) { w.type = d.exactType.id; });
        self.persist(); self.render();
      });
      esc.appendChild(escBtn);
      b.appendChild(esc);
    }

    var grid = el("div", "ezsf__fmt-grid");

    var pc = el("button", "ezsf__fmt" + (this.style === "precut" ? " is-sel" : "") + (d.precutAvail ? "" : " is-off"));
    pc.type = "button"; pc.disabled = !d.precutAvail;
    var pimg = el("span", "ezsf__fmt-img"); if (this.cfg.images && this.cfg.images.precut) pimg.style.backgroundImage = "url('" + this.cfg.images.precut + "')";
    pc.appendChild(pimg);
    pc.appendChild(el("span", "ezsf__fmt-name", "Precut"));
    pc.appendChild(el("span", "ezsf__fmt-desc", "Ready-made panels. Trim to fit."));
    pc.appendChild(el("span", "ezsf__fmt-price", d.precutAvail ? money(d.precutTotal) : "-"));
    if (d.precutAvail) {
      // Snaps ship INSIDE each precut bundle — the smallest SNA pack sized to that panel (BOM rule);
      // total = sum of the per-precut bundled packs (precutPackSize), matching how the roll shows its count.
      var precutSnapCt = d.validWins.reduce(function (s, w) {
        return s + (w.match ? self.shade.precutPackSize(w.match.w, w.match.h) : 0) * w.qty;
      }, 0);
      // B1/B2 + C3: the ship line, prominent at the choice. One panel -> its canonical (big×small) size so a
      // 24×16 and a 16×24 window read the SAME; several -> a count. Snap count gets its own pronounced pill (D4).
      var pShip;
      if (d.panels.length === 1 && d.validWins[0] && d.validWins[0].match) {
        var m0 = d.validWins[0].match, mb = Math.max(m0.w, m0.h), ms = Math.min(m0.w, m0.h);
        pShip = "Ships as " + mb + "×" + ms + " in, trim to fit";
      } else {
        pShip = d.panels.length + " ready-made panels, trim to fit";
      }
      pc.appendChild(el("span", "ezsf__fmt-ship", pShip));
      pc.appendChild(el("span", "ezsf__fmt-snaps", "Includes " + precutSnapCt + " snaps"));
      // C1(b): exact-fit types (perSide 0) carry no overage — the precut is the nearest stocked size AT or
      // ABOVE the opening. On a single exact-fit window that lands larger than the glass, say so, so an exact
      // match isn't expected. The ship line already carries "trim to fit"; this only explains the size.
      var w0 = d.validWins[0];
      if (d.panels.length === 1 && w0 && w0.type && w0.type.perSide === 0 && w0.match &&
          (w0.match.w > w0.fab.w || w0.match.h > w0.fab.h)) {
        pc.appendChild(el("span", "ezsf__fmt-note", "Nearest stocked size at or above your " + w0.W + "×" + w0.H + " in opening."));
      }
    } else {
      pc.appendChild(el("span", "ezsf__fmt-warn", "Not available for your window configuration"));
    }
    pc.addEventListener("click", function () { if (d.precutAvail) self.setState({ style: "precut" }); });
    grid.appendChild(pc);

    var rc = el("button", "ezsf__fmt" + (this.style === "roll" ? " is-sel" : "") + (d.rollAvail ? "" : " is-off"));
    rc.type = "button"; rc.disabled = !d.rollAvail;
    var rimg = el("span", "ezsf__fmt-img"); if (this.cfg.images && this.cfg.images.roll) rimg.style.backgroundImage = "url('" + this.cfg.images.roll + "')";
    rc.appendChild(rimg);
    rc.appendChild(el("span", "ezsf__fmt-name", "Roll"));
    rc.appendChild(el("span", "ezsf__fmt-desc", "6 ft-wide roll, cut shades to size. Snaps included to match your layout."));
    rc.appendChild(el("span", "ezsf__fmt-price", d.rollAvail ? money(d.rollTotal) : "-"));
    if (d.rollAvail) {
      // Snaps ARE included in the roll total (buildLines adds the SNA packs); surface the count.
      var rollSnapCt = (d.rollSnapPacks || []).reduce(function (s, p) { return s + p.n * p.count; }, 0);
      // FIX 3a — make the multi-roll purchase unmistakable (you're buying N separate rolls, not one).
      var _rollList = d.nest.rolls.map(function (f) { return f + " ft"; }).join(" + ");
      rc.appendChild(el("span", "ezsf__fmt-ship", d.nest.rolls.length > 1
        ? "You'll receive " + d.nest.rolls.length + " rolls: " + _rollList + " · 6 ft-wide, cut to fit"
        : _rollList + " of 6 ft-wide roll, cut to fit"));
      rc.appendChild(el("span", "ezsf__fmt-snaps", "Includes " + rollSnapCt + " snaps"));
    } else {
      // Distinguish the two unbuildable cases so the shopper knows what to do (no seaming: split long windows).
      var reason = d.nest && d.nest.oversizeReason;
      var maxFt = (d.nest && d.nest.maxRollIn) ? Math.floor(d.nest.maxRollIn / 12) : 25;
      var warn = reason === "length"
        ? "One window is longer than a " + maxFt + " ft roll: split it into panels up to " + maxFt + " ft (add each section as its own window)."
        : reason === "width"
        ? (d.exactFits
            ? "A window is wider than the 72 in roll once the +1″/side allowance is added: use the exact-size switch above, or split it."
            : "A window is wider than the 72 in roll: trim it down or split it.")
        : "A window is too large for the roll.";
      rc.appendChild(el("span", "ezsf__fmt-warn", warn));
    }
    rc.addEventListener("click", function () { if (d.rollAvail) self.setState({ style: "roll" }); });
    grid.appendChild(rc);

    b.appendChild(grid);
    // Per-precut breakdown so each window maps back to what the customer entered, with a count per precut
    // (shoppers often order the same window dims). Precut-only + only when there's more than one panel, so it
    // never overloads the section (a single-panel build is already spelled out on the card above).
    if (self.style === "precut" && d.precutAvail && d.panels.length > 1) {
      var pgroups = self.precutGroups(d);
      var totalPanels = pgroups.reduce(function (s, g) { return s + g.n; }, 0);
      var lwrap = el("div", "ezsf__fmt-list");
      var lhead = el("div", "ezsf__fmt-list-head");
      lhead.appendChild(el("span", "ezsf__fmt-list-h", "Your precut panels"));
      lhead.appendChild(el("span", "ezsf__fmt-list-total", totalPanels + " panel" + (totalPanels === 1 ? "" : "s")));
      lwrap.appendChild(lhead);
      pgroups.forEach(function (g) {
        // orient the precut to its window(s), same as the Build diagram, so the two never disagree.
        var portrait = g.port > 0 && g.land === 0;
        var pw = portrait ? g.psmall : g.pbig, ph = portrait ? g.pbig : g.psmall;
        var lr = el("div", "ezsf__fmt-listrow");
        var main = el("div", "ezsf__fmt-listmain");
        main.appendChild(el("span", "ezsf__fmt-listsz", "Precut " + pw + "×" + ph + " in"));
        var wins = g.winList.length === 1
          ? "fits your " + g.winList[0].W + "×" + g.winList[0].H + " window"
          : "fits " + g.winList.map(function (x) { return x.W + "×" + x.H + " ×" + x.n; }).join(", ");
        main.appendChild(el("span", "ezsf__fmt-listwin", wins));
        lr.appendChild(main);
        lr.appendChild(el("span", "ezsf__fmt-listct", "×" + g.n));
        lwrap.appendChild(lr);
      });
      b.appendChild(lwrap);
    }
    b.appendChild(this.stepContinue("Continue to Add-ons →", 5, !this.style));
  };

  // ---- STEP 5 body ----
  ShadeFinder.prototype.buildAddons = function (b, d) {
    var self = this;
    // app-aware "match your <surface>" noun — the snap studs stay mounted after the shade comes off, so a
    // shopper matches them to where they're visible (RV body / house trim / roof / boat), not the mesh.
    var STUD_MATCH = { rv: 'RV body', window: 'window frame', skylight: 'roof', boat: 'boat body' };
    var matchNoun = STUD_MATCH[this.app] || 'mounting surface';
    // studs — ROLL ONLY. Precut shades are matched-only (cap = stud = mesh, owner 2026-10-07): no stud-color
    // swatches, just a short note. The roll row below is unchanged.
    if (this.style === "precut") {
      b.appendChild(el("p", "ezsf__note", "Snaps match your mesh color. Each precut ships with its caps, pins and studs in " + esc(this.colorName()) + "."));
    } else b.appendChild(this.addonRow({
      on: this.studsOn, title: "Match studs to your " + matchNoun,
      sub: "Your studs ship in the mesh color by default. Swap them to match your " + matchNoun + " for a small add-on, so the mounts disappear when the screen is off. Recommended: " + d.studCount + " studs, sized to cover your build.",
      price: this.studsOn ? d.studCost : null,
      // D1: optional, never-forced education. Explains what a stud is + the default-black / colour-match point.
      // Function + selection stay in the row; this only teaches. (Illustration slots into .ezsf__studedu-txt later.)
      edu: function (main) {
        var det = el("details", "ezsf__studedu");
        if (self._studEduOpen) det.open = true;
        det.addEventListener("toggle", function () { self._studEduOpen = det.open; });
        det.appendChild(el("summary", "ezsf__studedu-sum", "What's a stud?"));
        det.appendChild(el("p", "ezsf__studedu-txt", "A stud is the small adhesive base that stays on your " + esc(matchNoun) + " year-round. The shade snaps onto it and pops off in seconds for cleaning or the off-season. Studs ship black to match the mesh by default. If they would show against a lighter " + esc(matchNoun) + ", swap them to a matching color so they blend in."));
        main.appendChild(det);
      },
      toggle: function () { self.setState({ studsOn: !self.studsOn }); },
      extra: function (wrap) {
        var row = el("div", "ezsf__studcolors");
        self.studHw.forEach(function (s) {
          var opt = el("button", "ezsf__studc" + (self.studColor === s.code ? " is-sel" : ""));
          opt.type = "button";
          var dot = el("span", "ezsf__studc-dot"); dot.style.background = s.c; opt.appendChild(dot);
          opt.appendChild(el("span", "ezsf__studc-name", esc(s.name)));
          opt.addEventListener("click", function () { self.setState({ studColor: s.code }); });
          row.appendChild(opt);
        });
        wrap.appendChild(row);
      },
    }));
    // bag
    b.appendChild(this.addonRow({
      on: this.bagQty > 0, title: "Shade storage bag", sub: "79 in bag for off-season storage.",
      price: d.bagCost || null,
      toggle: function () { self.setState({ bagQty: self.bagQty > 0 ? 0 : 1 }); },
      extra: this.bagQty > 0 ? function (wrap) {
        var q = el("div", "ezsf__qty");
        q.appendChild(el("span", "ezsf__qty-lab", "Qty"));
        var box = el("div", "ezsf__qty-box");
        var minus = el("button", "ezsf__qty-btn", "–"); minus.type = "button";
        minus.addEventListener("click", function () { self.setState({ bagQty: Math.max(1, self.bagQty - 1) }); });
        var val = el("span", "ezsf__qty-val", String(self.bagQty));
        var plus = el("button", "ezsf__qty-btn", "+"); plus.type = "button";
        plus.addEventListener("click", function () { self.setState({ bagQty: self.bagQty + 1 }); });
        box.appendChild(minus); box.appendChild(val); box.appendChild(plus);
        q.appendChild(box); wrap.appendChild(q);
      } : null,
    }));
    // airflo (dome only)
    if (d.domePresent) {
      b.appendChild(this.addonRow({
        on: this.airOn, title: "Airflow spacers",
        sub: "Hold the mesh off your domed skylight for airflow. Recommended: " + d.airQty + " for your dome" + (d.airQty === 1 ? "" : "s") + ".",
        price: d.airCost || null,
        toggle: function () { self.setState({ airOn: !self.airOn }); },
      }));
    }
    var rev = el("button", "ezsf__review", "Review order →");
    rev.type = "button";
    rev.addEventListener("click", function () { self.setState({ open: 6 }); });
    b.appendChild(rev);
  };

  ShadeFinder.prototype.addonRow = function (o) {
    var row = el("div", "ezsf__addon" + (o.on ? " is-on" : ""));
    var top = el("div", "ezsf__addon-top");
    var main = el("div", "ezsf__addon-main");
    var line = el("div", "ezsf__addon-line");
    var box = el("button", "ezsf__addon-box" + (o.on ? " is-on" : ""), o.on ? "✓" : "");
    box.type = "button";
    box.addEventListener("click", o.toggle);
    line.appendChild(box);
    line.appendChild(el("span", "ezsf__addon-title", esc(o.title)));
    main.appendChild(line);
    main.appendChild(el("div", "ezsf__addon-sub", esc(o.sub)));
    if (o.edu) o.edu(main);
    top.appendChild(main);
    if (o.price != null) top.appendChild(el("span", "ezsf__addon-price", money(o.price)));
    row.appendChild(top);
    if (o.on && o.extra) { var wrap = el("div", "ezsf__addon-extra"); o.extra(wrap); row.appendChild(wrap); }
    return row;
  };

  // ---- STEP 6 body ----
  // ---- "Email me this build" → Klaviyo "Submitted Configuration" event ----
  // build_url is window.location.href, which now carries the full selection (?app&c&w&fmt…) thanks to
  // the URL-persistence above — so the recap email's "Resume your build" link reopens the exact build.
  ShadeFinder.prototype.emailBuild = function (email, d) {
    var lines = this.buildLines(d);
    var summary = lines.map(function (l) { return l.qty + "× " + l.title + ". " + money(l.price * l.qty); }).join("\n");
    var meta = this.appMeta[this.app] || {};
    var appLabel = meta.label || (this.appObj() && this.appObj().label) || this.app;
    var fmt = this.style === "precut" ? "Precut" : this.style === "roll" ? "Roll" : "";
    // Generic label/value spec list — the ONE recap template loops this, so it renders any
    // configurator (skirting sends Length/Height/Color/Add-ons, hitch its own, etc.).
    var specs = [
      { label: "Application", value: appLabel },
      { label: "Mesh color", value: this.colorName() },
      { label: "Format", value: fmt },
      { label: "Windows", value: String(d.validWins.reduce(function (s, w) { return s + (w.qty || 1); }, 0)) },
    ];
    // Add-ons row is DYNAMIC and gated on the SAME conditions buildLines uses — so the summary
    // mirrors the cart, and the row is omitted entirely when nothing is added (no empty container;
    // the template just loops specs, so an absent entry renders nothing).
    var addons = [];
    if (this.studsActive()) addons.push("Body-color studs");
    if (this.bagQty > 0) addons.push("Storage bag" + (this.bagQty > 1 ? " ×" + this.bagQty : ""));
    if (this.airOn && d.airQty > 0) addons.push("Airflow spacers");
    if (addons.length) specs.push({ label: "Add-ons", value: addons.join(" · ") });
    // Non-line-item kit extras (snaps/studs are their own priced line items; this is the install kit that
    // ships in every order) — same string as the on-page "Install kit" row (sourced, owner-approved).
    specs.push({ label: "Every order includes", value: "Primer pen, marking pencil & removal tool" });
    var props = {
      customer_email: email,                       // explicit in the payload for trace-back + team notify
      config_type: "shade",
      product_noun: "shade",                       // drives the headline: "Your {noun} build, saved."
      specs: specs,
      // Sourced claims for THIS product only (never shown on another product's recap).
      value_points: ["Blocks up to 90% of heat", "3M™ VHB no-drill snap", "Limited Lifetime warranty"],
      // Universal across every configurator:
      total: d.grand,
      total_display: money(d.grand),               // currency-aware: "CA$2,420" for a Canadian shopper
      currency: ccyCode(),                         // ISO code of total/price/amount (presentment currency)
      build_url: window.location.href,
      line_items: lines.map(function (l) { return { title: l.title, sku: l.sku, qty: l.qty, price: l.price, amount: money(l.price * l.qty), opts: l.opts || "" }; }),
      summary: summary,
      // Kept flat for the team notification / analytics convenience:
      application: this.app,
      color: this.colorName(),
      format: fmt,
      window_count: d.validWins.length,
    };
    // EXACT cut nest (roll bins + panel positions) — piggybacks on the engine's OWN result so a
    // downstream reader (Quote Desk) renders the builder's cut layout 100%, never re-derived.
    // Guarded: only for a valid roll nest (precut / oversize carry none, and the reader falls back).
    if (d.nest && !d.nest.oversize && d.nest.rollBins && d.nest.rollBins.length) {
      props.nest = {
        rolls: (d.nest.rolls || []).slice(),
        wastePct: d.nest.wastePct,
        bins: d.nest.rollBins.map(function (b) {
          return { ft: b.rollFt, usedIn: b.usedIn, items: (b.items || []).map(function (it) {
            return { w: it.fabW, h: it.fabH, ww: it.winW, wh: it.winH, wi: it.wi, x: it.x, y: it.y, r: it.rot ? 1 : 0 };
          }) };
        }),
      };
    }
    hubspotQuoteSubmit(email, props);
    return klaviyoTrack("Submitted Configuration", email, props);
  };

  // ---- cut-sheet: serialize the current build (d) into the render endpoint's `nb` contract.
  //      STRUCTURAL MIRROR of cut-sheet/pipeline/normalize-engine.mjs computeNormalized() so the LIVE
  //      client and the BATCH producer emit the identical shape. Roll + snaps only (the format Troy
  //      approved); add-ons (studs/bag/airflo) are intentionally NOT on the cut-sheet. ----
  ShadeFinder.prototype.buildCutSheetNb = function (d) {
    var self = this;
    var PAL = ["#c4622a", "#1a1814", "#7a8b6f", "#a8743b", "#43506a", "#8a6d3f", "#5b6e8c", "#6d5630"];
    var num = function (x) { var v = Number(x); return isFinite(v) ? v : 0; };
    var color = this.colorName();
    var ref = "SH-" + Date.now().toString(36).toUpperCase().slice(-6);
    var date = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    var meta = { ref: ref, date: date, configType: "shade-roll", color: color, fromQuote: true, allowance: 0 };

    // schedule: merge validWins by opening size (sum qty); 8-colour PAL keyed by distinct-size index
    var map = {}, order = [];
    (d.validWins || []).forEach(function (w) {
      var key = w.W + "x" + w.H;
      if (map[key]) { map[key].qty += w.qty; }
      else { map[key] = { w: w.W, h: w.H, fabW: w.fab.w, fabH: w.fab.h, qty: w.qty, snaps: w.snaps }; order.push(key); }
    });
    var distinct = order.map(function (k) { return map[k]; });
    var schedule = distinct.map(function (dd, i) {
      return { w: dd.w, h: dd.h, fabW: dd.fabW, fabH: dd.fabH, qty: dd.qty, snaps: dd.snaps, color: PAL[i % PAL.length] };
    });
    var totalSnaps = num(d.totalSnaps), panelCount = (d.panels || []).length;

    // OVERSIZE: no cut plan; the endpoint renders the custom-solution page
    if (!d.nest || d.nest.oversize) {
      return { meta: meta, schedule: schedule,
        totals: { panels: panelCount, distinctSizes: distinct.length, snapsNeeded: totalSnaps, snapsShipped: 0, studsShipped: 0, screws: 0, spare: 0 },
        snapPacks: [], nest: { oversize: true, wastePct: 0, rolls: [], rollBins: [] },
        kit: [], order: { lines: [], subtotal: 0, shipping: 0 }, currency: ccyCode(), resumeUrl: window.location.href };
    }

    // rollBins -> contract shape, with the SAME empty->single-strip fallback cutDiagram uses
    var totalFt = (d.nest.rolls || []).reduce(function (s, f) { return s + f; }, 0);
    var enginBins = (d.nest.rollBins && d.nest.rollBins.length) ? d.nest.rollBins
      : [{ rollFt: totalFt, usedIn: d.nest.lengthIn, items: d.nest.placed }];
    var rollBins = enginBins.map(function (bn) {
      return { rollFt: num(bn.rollFt), usedIn: num(bn.usedIn),
        items: (bn.items || []).map(function (it) { return { fabW: num(it.fabW), fabH: num(it.fabH), x: num(it.x), y: num(it.y), rot: !!it.rot }; }) };
    });
    var rolls = (d.nest.rolls && d.nest.rolls.length) ? d.nest.rolls.slice() : rollBins.map(function (bn) { return bn.rollFt; });
    var nestOut = { oversize: false, wastePct: num(d.nest.wastePct), rolls: rolls, rollBins: rollBins };

    var snapPacks = (d.rollSnapPacks || []).map(function (p) { return { n: num(p.n), count: num(p.count) }; });
    var snapsShipped = snapPacks.length ? snapPacks.reduce(function (s, p) { return s + p.n * p.count; }, 0) : totalSnaps;
    var spare = Math.max(0, snapsShipped - totalSnaps);
    var screws = Math.round(snapsShipped / 2);
    var packLabel = snapPacks.length ? snapPacks.map(function (p) { return p.count + "×" + p.n + "-ct"; }).join(" + ") : (snapsShipped + " ct");
    var totals = { panels: panelCount, distinctSizes: distinct.length, snapsNeeded: totalSnaps, snapsShipped: snapsShipped, studsShipped: snapsShipped, screws: screws, spare: spare };

    var kit = [
      { qty: rollBins.length, item: rollBins.length > 1 ? "Shade Mesh Rolls" : "Shade Mesh Roll", spec: "72″ wide, cut to size", note: "Cut per the pattern." },
      { qty: snapsShipped, item: "Adhesive Snap Fasteners", spec: "no-drill 3M VHB", note: "Ships as " + packLabel + "." + (spare ? " " + spare + " spare over the " + totalSnaps + " needed." : "") + " Full strength in about 72 hours." },
      { qty: snapsShipped, item: "Snap Caps", spec: "color-matched to your " + color + " mesh", note: "One per fastener, snaps over the mesh onto each stud." + (spare ? " " + spare + " spare." : "") },
      { qty: screws, item: "Stainless Screws", spec: "free failsafe", note: "1 per 2 fasteners, only if you ever want a mechanical backup." },
      { qty: 1, item: "Cap & Removal Tool", spec: "reusable", note: "Seats caps, and separates each pin from its stud for seasonal removal." }
    ];

    // order: rolls (grouped by ft) + snap packs, extended prices (roll + snaps only)
    var groups = {}; rollBins.forEach(function (bn) { groups[bn.rollFt] = (groups[bn.rollFt] || 0) + 1; });
    var rollLines = Object.keys(groups).map(Number).sort(function (a, b) { return a - b; }).map(function (ft) {
      var e = self.rollEntry(ft), cnt = groups[ft], price = self.rollPrice(ft) || 0;
      return { title: "Shade Mesh Roll · " + ft + " ft", sub: (e && e.sku ? e.sku : "roll") + " · ×" + cnt, price: price * cnt };
    });
    var snapLines = snapPacks.map(function (p) {
      return { title: "Snap Fasteners · " + p.n + " ct", sub: "×" + p.count, price: (self.snaPrice(p.n) || 0) * p.count };
    });
    // selected add-ons (studs / bag / airflo) — PRICED SUMMARY only, so the sheet's total reconciles
    // with the cart (d.grand). The cut pattern / schedule / "what ships" kit stay mesh-only.
    var addonLines = [];
    if (self.studsOn) (d.studPacks || []).forEach(function (p) {
      addonLines.push({ title: "Body-Color Stud Pack · " + p.n + " ct", sub: "off-color studs · ×" + p.count, price: (self.snsPrice(p.n) || 0) * p.count });
    });
    if (self.bagQty > 0 && self.acc && self.acc.storageBag) addonLines.push({ title: self.acc.storageBag.name, sub: "×" + self.bagQty, price: (self.acc.storageBag.price || 0) * self.bagQty });
    if (self.airOn && d.airQty > 0 && self.acc && self.acc.airflo) addonLines.push({ title: self.acc.airflo.name, sub: "dome spacer · ×" + d.airQty, price: (self.acc.airflo.price || 0) * d.airQty });
    var lines = rollLines.concat(snapLines, addonLines);
    var subtotal = lines.reduce(function (s, l) { return s + num(l.price); }, 0);
    // USD: the store's real flat-rate ladder ($9.95 under $50 · $14.95 under $70 · $19.95 under the free
    // threshold · free at $225+). Non-USD: rates are USD converted at checkout, so DON'T estimate —
    // shipping is null (unknown) and `currency` tells the renderer what the prices are in.
    var shipping = null;
    if (isBaseCcy()) {
      var fsOver = this.cfg.freeShipOver || 225;
      shipping = subtotal < 50 ? 9.95 : subtotal < 70 ? 14.95 : subtotal < fsOver ? 19.95 : 0;
    }

    return { meta: meta, schedule: schedule, totals: totals, snapPacks: snapPacks, nest: nestOut,
      kit: kit, order: { lines: lines, subtotal: subtotal, shipping: shipping }, currency: ccyCode(), resumeUrl: window.location.href };
  };

  ShadeFinder.prototype.CUTSHEET_ENDPOINT = "https://ezsnap-cutsheet.vercel.app/cut-sheet";
  ShadeFinder.prototype.downloadCutSheet = function (d, btn, msg) {
    var nb;
    try { nb = this.buildCutSheetNb(d); }
    catch (e) { if (msg) { msg.textContent = "Couldn’t build your cut-sheet. Please try again."; msg.className = "ezsf__emailshare-msg is-err"; } return; }
    var orig = btn ? btn.textContent : "";
    if (btn) { btn.disabled = true; btn.textContent = "Preparing your cut-sheet…"; }
    if (msg) { msg.textContent = ""; msg.className = "ezsf__emailshare-msg"; }
    fetch(this.CUTSHEET_ENDPOINT, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ build: nb }) })
      .then(function (r) { if (!r.ok) throw new Error("HTTP " + r.status); return r.blob(); })
      .then(function (blob) {
        var url = URL.createObjectURL(blob), a = document.createElement("a");
        a.href = url; a.download = "EZSnap-cut-sheet-" + nb.meta.ref + ".pdf";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(function () { URL.revokeObjectURL(url); }, 4000);
        if (btn) { btn.disabled = false; btn.textContent = "Cut-sheet downloaded ✓"; }
        if (msg) { msg.textContent = "Saved to your device."; msg.className = "ezsf__emailshare-msg is-ok"; }
      })
      .catch(function () {
        if (btn) { btn.disabled = false; btn.textContent = orig; }
        if (msg) { msg.textContent = "Sorry, the cut-sheet didn’t generate. Please try again."; msg.className = "ezsf__emailshare-msg is-err"; }
      });
  };

  ShadeFinder.prototype.buildEmailShare = function (b, d) {
    var self = this;
    var wrap = el("div", "ezsf__emailshare");
    wrap.appendChild(el("div", "ezsf__emailshare-h", "Email me this build"));
    wrap.appendChild(el("div", "ezsf__emailshare-sub",
      "Not ready to order? We’ll send your configuration to your inbox with a link to pick up right where you left off."));
    var row = el("div", "ezsf__emailshare-row");
    var input = el("input", "ezsf__emailshare-input");
    input.type = "email"; input.placeholder = "you@email.com";
    input.setAttribute("inputmode", "email"); input.autocomplete = "email"; input.setAttribute("aria-label", "Your email");
    var btn = el("button", "ezsf__emailshare-btn", "Email my build →");
    btn.type = "button";
    var msg = el("div", "ezsf__emailshare-msg"); msg.setAttribute("role", "status");
    var send = function () {
      var email = (input.value || "").trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        msg.textContent = "Please enter a valid email."; msg.className = "ezsf__emailshare-msg is-err"; input.focus(); return;
      }
      btn.disabled = true; input.disabled = true;
      var ok = self.emailBuild(email, d);
      if (ok) {
        msg.textContent = "Sent ✓ Check your inbox for your build."; msg.className = "ezsf__emailshare-msg is-ok";
        btn.textContent = "Sent ✓";
      } else {
        msg.textContent = "Sorry, that didn’t send. Please try again."; msg.className = "ezsf__emailshare-msg is-err";
        btn.disabled = false; input.disabled = false;
      }
    };
    btn.addEventListener("click", send);
    input.addEventListener("keydown", function (e) { if (e.key === "Enter") { e.preventDefault(); send(); } });
    row.appendChild(input); row.appendChild(btn);
    wrap.appendChild(row);
    wrap.appendChild(msg);
    b.appendChild(wrap);
  };

  ShadeFinder.prototype.buildOrder = function (b, d) {
    var self = this;
    var lines = this.buildLines(d);
    lines.forEach(function (l, i) {
      var row = el("div", "ezsf__line" + (i ? " ezsf__line--sep" : ""));
      var lft = el("div");
      lft.appendChild(el("div", "ezsf__line-title", esc(l.title)));
      lft.appendChild(el("div", "ezsf__line-meta", esc(l.sku) + " · " + esc(l.opts) + " · ×" + l.qty));
      row.appendChild(lft);
      row.appendChild(el("div", "ezsf__line-price", money(l.price * l.qty)));
      b.appendChild(row);
    });

    if (this.style === "roll" && d.nest) b.appendChild(this.cutDiagram(d.nest, false));

    var total = el("div", "ezsf__total");
    var tl = el("div");
    tl.appendChild(el("div", "ezsf__total-lab", "Order total" + (d.freeShip ? " · free shipping" : "")));
    tl.appendChild(el("div", "ezsf__total-val", money(d.grand)));
    total.appendChild(tl);
    var atc = el("button", "ezsf__atc", "Add all to cart →");
    atc.type = "button";
    atc.addEventListener("click", function () { self.addToCart(); });
    total.appendChild(atc);
    b.appendChild(total);
    if (this.atcError) {
      var errEl = el("div", null, esc(this.atcError));
      errEl.setAttribute("role", "alert");
      errEl.style.cssText = "margin-top:10px;padding:10px 12px;border-radius:8px;background:rgba(196,98,42,.09);color:#8a2b12;font-size:13px;line-height:1.45;";
      b.appendChild(errEl);
    }

    if (this.style === "roll" && d.nest) {
      var expand = el("button", "ezsf__diag-expand", "View full cut pattern ⤢");
      expand.type = "button";
      expand.addEventListener("click", function () { self.openDiagModal(d.nest); });
      b.appendChild(expand);
    }

    // Download your cut-sheet (PDF) — serialize the build and POST to the render endpoint. Roll builds
    // with a real cut plan only (oversize has no pattern); sits beside "View full cut pattern".
    if (this.style === "roll" && d.nest && !d.nest.oversize) {
      var dlWrap = el("div", "ezsf__cutsheet-dl");   // block wrapper -> download sits on its OWN line below "View full cut pattern"
      var dl = el("button", "ezsf__diag-expand", "Download your cut-sheet (PDF) ↓");
      dl.type = "button";
      var dlMsg = el("div", "ezsf__emailshare-msg"); dlMsg.setAttribute("role", "status");
      dl.addEventListener("click", function () { self.downloadCutSheet(d, dl, dlMsg); });
      dlWrap.appendChild(dl); dlWrap.appendChild(dlMsg);
      b.appendChild(dlWrap);
    }

    this.buildEmailShare(b, d);
  };

  // ---- roll cut-pattern SVG diagram — ONE segment per PHYSICAL ROLL ----
  // Each panel is cut whole from one roll (no seaming), so the diagram splits into a segment per
  // stock roll the shopper buys; the gap between segments IS the roll boundary (never cut across it).
  ShadeFinder.prototype.cutDiagram = function (nest, big, titleOverride, availW) {
    if (!nest || !nest.placed.length) return el("div");
    var W = this.cfg.rollWidthIn || 72;
    var PAL = ["#c4622a", "#1a1814", "#7a8b6f", "#a8743b", "#5b6e8c"];
    var svgNS = "http://www.w3.org/2000/svg";

    var wrap = el("div", "ezsf__diag" + (big ? " ezsf__diag--big" : ""));
    var nRolls = nest.rolls.length;
    var head = el("div", "ezsf__diag-head");
    head.appendChild(el("span", "ezsf__diag-title",
      titleOverride || ("Cut pattern · " + nest.rolls.map(function (f) { return f + "ft"; }).join(" + ") +
      " roll" + (nRolls > 1 ? "s" : "") + " · " + Math.round(100 - nest.wastePct) + "% used")));
    wrap.appendChild(head);

    // one bin = one physical roll (engine's rollBins); fall back to a single continuous strip
    var totalFt = nest.rolls.reduce(function (s, f) { return s + f; }, 0);
    var bins = (nest.rollBins && nest.rollBins.length)
      ? nest.rollBins
      : [{ rollFt: totalFt, usedIn: nest.lengthIn, items: nest.placed }];
    var multi = bins.length > 1;
    var perW;
    if (!big && availW) {
      // Fill the rail width (like the precut preview): size the roll strip(s) to the container, cap the
      // tallest strip's height so a long roll doesn't run away, floor so a busy multi-roll stays legible.
      var _avail = Math.max(160, availW - 34); // 28 = gallery-build L+R padding, +6 for the svg's 1px border + slack
      var _maxRollIn = bins.reduce(function (m, bn) { return Math.max(m, bn.rollFt * 12); }, 1);
      perW = Math.max(90, Math.min((_avail - (bins.length - 1) * 14) / bins.length, 560 * W / _maxRollIn, 500));
    } else {
      perW = multi ? (big ? 210 : 128) : (big ? 460 : 300);
    }

    function drawPanel(svg, p) {
      var fw = p.rot ? p.fabH : p.fabW, fh = p.rot ? p.fabW : p.fabH;
      var ww = p.rot ? p.winH : p.winW, wh = p.rot ? p.winW : p.winH;
      var col = PAL[(p.wi || 0) % PAL.length];
      var g = document.createElementNS(svgNS, "g");
      var r1 = document.createElementNS(svgNS, "rect");
      // FIX — draw at the true 0–72 grid (no +0.5 shift). The old offset pushed panels flush to the
      // roll's RIGHT edge out to 72.5, so the viewBox clipped their right border ("cut off" look).
      r1.setAttribute("x", p.x); r1.setAttribute("y", p.y);
      r1.setAttribute("width", fw); r1.setAttribute("height", fh);
      r1.setAttribute("fill", col); r1.setAttribute("fill-opacity", "0.07");
      r1.setAttribute("stroke", col); r1.setAttribute("stroke-width", "0.7");
      g.appendChild(r1);
      if (ww && wh) {
        var r2 = document.createElementNS(svgNS, "rect");
        r2.setAttribute("x", p.x + (fw - ww) / 2); r2.setAttribute("y", p.y + (fh - wh) / 2);
        r2.setAttribute("width", ww); r2.setAttribute("height", wh);
        r2.setAttribute("fill", "none"); r2.setAttribute("stroke", col);
        r2.setAttribute("stroke-width", "0.5"); r2.setAttribute("stroke-dasharray", "2 1.5");
        g.appendChild(r2);
      }
      var tx = document.createElementNS(svgNS, "text");
      tx.setAttribute("x", p.x + fw / 2); tx.setAttribute("y", p.y + fh / 2 + 1.5);
      tx.setAttribute("text-anchor", "middle"); tx.setAttribute("font-size", Math.min(4.5, fw / 4));
      tx.setAttribute("font-weight", "700"); tx.setAttribute("fill", col);
      tx.textContent = p.fabW + "×" + p.fabH;
      g.appendChild(tx);
      svg.appendChild(g);
    }

    var row = el("div", "ezsf__diag-rolls");
    row.style.cssText = "display:flex;gap:" + (big ? 22 : 14) + "px;align-items:flex-start;overflow-x:auto;padding-bottom:4px";
    bins.forEach(function (bin, bi) {
      var rollLenIn = bin.rollFt * 12;
      var seg = el("div", "ezsf__diag-roll");
      seg.style.cssText = "flex:0 0 auto;text-align:center";
      if (multi) {
        var lbl = el("div", "ezsf__diag-rolllabel", "Roll " + (bi + 1) + " · " + bin.rollFt + "ft");
        lbl.style.cssText = "font-size:11px;font-weight:700;letter-spacing:.04em;color:#8a7d6b;margin-bottom:5px;text-transform:uppercase";
        seg.appendChild(lbl);
      }
      // ROLL GRAPHIC — a little cylinder the mesh unrolls FROM, so the strip reads as a real 72"-wide
      // roll with an obvious START at the top (illustrative; the scaled cut layout is the svg below).
      var rhH = big ? 34 : 28, cylTop = 4, cylH = Math.round(rhH * 0.5);
      var rh = document.createElementNS(svgNS, "svg");
      rh.setAttribute("width", perW); rh.setAttribute("height", rhH);
      rh.setAttribute("viewBox", "0 0 " + perW + " " + rhH);
      rh.setAttribute("class", "ezsf__diag-rollhead"); rh.setAttribute("style", "display:block;margin:0 auto;overflow:visible");
      var rhDefs = document.createElementNS(svgNS, "defs");
      var rhLg = document.createElementNS(svgNS, "linearGradient");
      var rhId = "ezroll_" + bi + "_" + Math.round(perW * 10);
      rhLg.setAttribute("id", rhId); rhLg.setAttribute("x1", "0"); rhLg.setAttribute("y1", "0"); rhLg.setAttribute("x2", "0"); rhLg.setAttribute("y2", "1");
      [["0", "#efe4d4"], ["0.42", "#faf5ef"], ["1", "#d3c3aa"]].forEach(function (sp) { var s2 = document.createElementNS(svgNS, "stop"); s2.setAttribute("offset", sp[0]); s2.setAttribute("stop-color", sp[1]); rhLg.appendChild(s2); });
      rhDefs.appendChild(rhLg); rh.appendChild(rhDefs);
      var rBody = document.createElementNS(svgNS, "rect");
      rBody.setAttribute("x", 1); rBody.setAttribute("y", cylTop); rBody.setAttribute("width", perW - 2); rBody.setAttribute("height", cylH); rBody.setAttribute("rx", cylH / 2);
      rBody.setAttribute("fill", "url(#" + rhId + ")"); rBody.setAttribute("stroke", "#c4622a"); rBody.setAttribute("stroke-width", "1");
      rh.appendChild(rBody);
      var rCy = cylTop + cylH / 2, rCr = cylH / 2 - 0.5;
      [[rCr, "#faf5ef", "1"], [rCr * 0.58, "none", "0.8"], [rCr * 0.24, "none", "0.8"]].forEach(function (cc) { var c = document.createElementNS(svgNS, "circle"); c.setAttribute("cx", rCy + 1.5); c.setAttribute("cy", rCy); c.setAttribute("r", cc[0]); c.setAttribute("fill", cc[1]); c.setAttribute("stroke", "#c4622a"); c.setAttribute("stroke-width", cc[2]); rh.appendChild(c); });
      var rCut = document.createElementNS(svgNS, "line");
      rCut.setAttribute("x1", 1); rCut.setAttribute("y1", cylTop + cylH + 3); rCut.setAttribute("x2", perW - 1); rCut.setAttribute("y2", cylTop + cylH + 3);
      rCut.setAttribute("stroke", "#b4a896"); rCut.setAttribute("stroke-width", "1"); rCut.setAttribute("stroke-dasharray", "4 3");
      rh.appendChild(rCut);
      seg.appendChild(rh);
      var rCap = el("div", "ezsf__diag-rollcap", "72″ wide · roll starts here ↓");
      rCap.setAttribute("style", "font-size:" + (big ? 10.5 : 9) + "px;letter-spacing:.03em;color:#8a7d6b;margin:1px 0 5px;text-transform:uppercase;font-weight:600");
      seg.appendChild(rCap);

      var svg = document.createElementNS(svgNS, "svg");
      svg.setAttribute("viewBox", "0 0 " + W + " " + rollLenIn);
      svg.setAttribute("class", "ezsf__diag-svg");
      svg.setAttribute("width", perW);
      svg.setAttribute("height", perW / W * rollLenIn);
      bin.items.forEach(function (p) { drawPanel(svg, p); });
      if (bin.usedIn < rollLenIn) {
        var st = document.createElementNS(svgNS, "text");
        st.setAttribute("x", W / 2); st.setAttribute("y", bin.usedIn + (rollLenIn - bin.usedIn) / 2);
        st.setAttribute("text-anchor", "middle"); st.setAttribute("font-size", "4"); st.setAttribute("fill", "#b4a896");
        st.textContent = "surplus";
        svg.appendChild(st);
      }
      seg.appendChild(svg);
      row.appendChild(seg);
    });
    wrap.appendChild(row);

    var legend = el("div", "ezsf__diag-legend");
    var legendItems = ["72″ wide roll", "solid line = material size", "- - - dashed = window opening"];
    if (multi) legendItems.push("each panel cut whole from ONE roll");
    // FIX 3b — panels are rotated to nest efficiently; a landscape-drawn panel can carry a portrait
    // label, which reads as sideways. Note it so the diagram doesn't look wrong to the customer.
    if ((nest.placed || []).some(function (p) { return p.rot; })) legendItems.push("some panels turned sideways to save material");
    legendItems.forEach(function (t) { legend.appendChild(el("span", null, esc(t))); });
    wrap.appendChild(legend);
    return wrap;
  };

  ShadeFinder.prototype.openDiagModal = function (nest) {
    var self = this;
    var back = el("div", "ezsf__modal");
    var panel = el("div", "ezsf__modal-panel");
    var head = el("div", "ezsf__modal-head");
    head.appendChild(el("span", "ezsf__modal-title", "Recommended cut pattern"));
    var close = el("button", "ezsf__modal-close", "✕"); close.type = "button";
    close.addEventListener("click", function () { back.remove(); });
    head.appendChild(close);
    panel.appendChild(head);
    panel.appendChild(this.cutDiagram(nest, true));
    panel.addEventListener("click", function (e) { e.stopPropagation(); });
    back.appendChild(panel);
    back.addEventListener("click", function () { back.remove(); });
    document.body.appendChild(back);
  };

  // Aggregate a precut build by precut SKU (canonical big×small). Precut is orientation-agnostic, so a 10×16
  // and a 16×10 window that resolve to the SAME precut collapse into ONE entry with a combined count. Each
  // group also keeps the distinct windows it covers (as entered) + the largest window footprint for overlay,
  // so the diagram and the Step-4 list can both map back per window. Shared by precutPreview + buildFormat.
  ShadeFinder.prototype.precutGroups = function (d) {
    var groups = {}, order = [];
    (d.validWins || []).forEach(function (w) {
      if (!w.match) return;
      var pbig = Math.max(w.match.w, w.match.h), psmall = Math.min(w.match.w, w.match.h);
      var key = pbig + "x" + psmall;
      if (!groups[key]) { groups[key] = { pbig: pbig, psmall: psmall, n: 0, wins: {}, wOrder: [], wi: w.i, cbig: 0, csmall: 0, port: 0, land: 0 }; order.push(key); }
      var g = groups[key];
      g.n += w.qty;
      if (w.W < w.H) g.port += w.qty; else g.land += w.qty;
      var wk = w.W + "x" + w.H;
      if (!g.wins[wk]) { g.wins[wk] = { W: w.W, H: w.H, n: 0 }; g.wOrder.push(wk); }
      g.wins[wk].n += w.qty;
      var cb = Math.max(w.W, w.H), cs = Math.min(w.W, w.H);
      if (cb * cs >= g.cbig * g.csmall) { g.cbig = cb; g.csmall = cs; } // largest window footprint -> overlay
    });
    return order.map(function (k) { var g = groups[k]; g.winList = g.wOrder.map(function (x) { return g.wins[x]; }); return g; });
  };

  // ---- precut per-panel preview — each precut SKU overlaid with the window it covers, so the shopper sees
  // the true surplus PER PANEL (the roll cut-diagram's nest + roll surplus is meaningless for precut).
  ShadeFinder.prototype.precutPreview = function (d, title, availW) {
    var svgNS = "http://www.w3.org/2000/svg";
    var PAL = ["#c4622a", "#1a1814", "#7a8b6f", "#a8743b", "#5b6e8c"];
    var wrap = el("div", "ezsf__diag");
    var head = el("div", "ezsf__diag-head");
    head.appendChild(el("span", "ezsf__diag-title", title || "Your panels · precut to size"));
    wrap.appendChild(head);

    var groups = this.precutGroups(d);
    if (!groups.length) return this.cutDiagram(d.nest, false, title); // safety: no precut matches -> roll view

    // Orient each group up front so ALL panels can be scaled to FILL the rail width (count-aware): a couple of
    // combos render large; many combos scale down and wrap. One shared scale keeps true relative sizes; the
    // height cap stops a tall portrait from dominating, the floor keeps panels legible when there are lots.
    // (Orientation: a single window orientation shows as-is; flipped/mixed collapses to canonical landscape,
    // since the two orientations share one precut.)
    var items = groups.map(function (g) {
      var portrait = g.port > 0 && g.land === 0;
      return { g: g, pw: portrait ? g.psmall : g.pbig, ph: portrait ? g.pbig : g.psmall,
        ww: portrait ? g.csmall : g.cbig, wh: portrait ? g.cbig : g.csmall };
    });
    var GAP = 16, avail = Math.max(160, (availW || 360) - 28); // 28 = .ezsf__gallery-build L+R padding
    var N = items.length, sumW = 0, maxH = 1;
    items.forEach(function (it) { sumW += it.pw; maxH = Math.max(maxH, it.ph); });
    // px-per-inch: fill the rail in one row, but never exceed the height cap or a sane max, never below a floor.
    var sc = Math.min(Math.max((avail - (N - 1) * GAP) / sumW, 3.4), 340 / maxH, 18);

    var row = el("div", "ezsf__pcard-row");
    items.forEach(function (it) {
      var g = it.g, col = PAL[(g.wi || 0) % PAL.length];
      var pw = it.pw, ph = it.ph, ww = it.ww, wh = it.wh;
      var cell = el("div", "ezsf__pcard");
      var svg = document.createElementNS(svgNS, "svg");
      svg.setAttribute("viewBox", "0 0 " + (pw + 2) + " " + (ph + 2));
      svg.setAttribute("width", Math.round(pw * sc)); svg.setAttribute("height", Math.round(ph * sc));
      svg.setAttribute("class", "ezsf__diag-svg");
      var r1 = document.createElementNS(svgNS, "rect");
      r1.setAttribute("x", 1); r1.setAttribute("y", 1); r1.setAttribute("width", pw); r1.setAttribute("height", ph);
      r1.setAttribute("fill", col); r1.setAttribute("fill-opacity", "0.07"); r1.setAttribute("stroke", col); r1.setAttribute("stroke-width", "0.7");
      svg.appendChild(r1);
      var r2 = document.createElementNS(svgNS, "rect");
      r2.setAttribute("x", 1 + (pw - ww) / 2); r2.setAttribute("y", 1 + (ph - wh) / 2);
      r2.setAttribute("width", ww); r2.setAttribute("height", wh);
      r2.setAttribute("fill", "none"); r2.setAttribute("stroke", col); r2.setAttribute("stroke-width", "0.5"); r2.setAttribute("stroke-dasharray", "2 1.5");
      svg.appendChild(r2);
      // Label BOTH sizes so the customer sees their input vs the precut: the window size sits centered in the
      // dashed opening; the precut size sits at the top of the panel (when the surplus band has room). The
      // caption names both again in words so it is unambiguous which is which.
      var topMargin = (ph - wh) / 2;
      var wtx = document.createElementNS(svgNS, "text");
      wtx.setAttribute("x", 1 + pw / 2); wtx.setAttribute("y", 1 + ph / 2 + 1.3);
      wtx.setAttribute("text-anchor", "middle"); wtx.setAttribute("font-size", Math.min(4, ww / 4));
      wtx.setAttribute("font-weight", "700"); wtx.setAttribute("fill", col);
      wtx.textContent = ww + "×" + wh;
      svg.appendChild(wtx);
      if (topMargin >= 2) {
        var ptx = document.createElementNS(svgNS, "text");
        ptx.setAttribute("x", 1 + pw / 2); ptx.setAttribute("y", 1 + Math.min(topMargin * 0.6, 2.2) + 0.7);
        ptx.setAttribute("text-anchor", "middle"); ptx.setAttribute("font-size", Math.min(2.3, pw / 9));
        ptx.setAttribute("font-weight", "700"); ptx.setAttribute("fill", col); ptx.setAttribute("fill-opacity", "0.7");
        ptx.textContent = pw + "×" + ph;
        svg.appendChild(ptx);
      }
      cell.appendChild(svg);
      // FIX — precut QUANTITY is the key buy number (esp. large orders); surface it as a prominent
      // badge + a bold count line, instead of the buried "· ×N" suffix on the fits-your caption.
      if (g.n > 1) cell.appendChild(el("div", "ezsf__pcard-qtybadge", "×" + g.n));
      var cap = el("div", "ezsf__pcard-cap");
      cap.appendChild(el("span", "ezsf__pcard-precut", "Precut " + pw + "×" + ph + " in"));
      cap.appendChild(el("span", "ezsf__pcard-qty", g.n + (g.n === 1 ? " panel" : " panels")));
      cap.appendChild(el("span", "ezsf__pcard-win", "fits your " + ww + "×" + wh + " window"));
      cell.appendChild(cap);
      row.appendChild(cell);
    });
    wrap.appendChild(row);

    var legend = el("div", "ezsf__diag-legend");
    ["solid line = precut panel", "- - - dashed = your window opening"].forEach(function (t) {
      legend.appendChild(el("span", null, esc(t)));
    });
    wrap.appendChild(legend);
    return wrap;
  };

  // ---- right-rail gallery + "every order includes" ----
  ShadeFinder.prototype.renderGallery = function (d, meta) {
    var self = this;
    var col = el("div", "ezsf__gallery");
    if (typeof this.activeView === "undefined") this.activeView = "gallery";
    // Cache the rail width whenever it's measurable (a prior gallery is mounted — e.g. during Step-3 live
    // typing). A full re-render clears the DOM before building, so nothing is measurable then; the cache
    // (warmed earlier) still lets the precut preview + roll diagram fill the real rail. Fallback pre-layout.
    var priorGal = this.root.querySelector(".ezsf__gallery");
    if (priorGal && priorGal.clientWidth) this._railW = priorGal.clientWidth;
    var railW = this._railW || (this.root.clientWidth > 760 ? Math.min(460, Math.round(this.root.clientWidth * 0.42)) : Math.max(280, this.root.clientWidth - 44));
    var photos = (this.directed && this.cfg.galleryPhotos && this.cfg.galleryPhotos.length) ? this.cfg.galleryPhotos
      : (meta.photos && meta.photos.length) ? meta.photos
      : (meta.img ? [meta.img] : []);

    // A "Build" view of the panels lives alongside the photo gallery, reachable via the Gallery/Build
    // toggle below whenever a valid build exists. DEFAULT IS THE PHOTO GALLERY and it stays there until the
    // shopper explicitly clicks "Build" (owner decision 2026-08-03). We never auto-flip on a valid build, so
    // a refresh with a saved/restored build still opens on the product photos (activeView isn't persisted).
    var canBuild = d.allValid && !!d.nest && !d.nest.oversize;
    if (this.activeView === "build" && !canBuild) this.activeView = "gallery";

    if (canBuild) {
      var nav = el("div", "ezsf__galnav");
      [["gallery", "Gallery"], ["build", "Build"]].forEach(function (v) {
        var btn = el("button", "ezsf__galnav-btn" + (self.activeView === v[0] ? " is-sel" : ""), v[1]);
        btn.type = "button";
        btn.addEventListener("click", function () { self.activeView = v[0]; self.render(); });
        nav.appendChild(btn);
      });
      col.appendChild(nav);
    }

    if (this.activeView === "build" && canBuild) {
      var bwrap = el("div", "ezsf__gallery-build");
      if (this.style === "precut") {
        // Precut ships as finished PER-WINDOW panels, so the meaningful surplus is per panel (the precut
        // size vs the window it covers), NOT the roll-nesting leftover. Show each window overlaid on the
        // precut size it ships as.
        bwrap.appendChild(this.precutPreview(d, "Your panels · precut to size", railW));
      } else {
        var btitle = this.style === "roll" ? undefined : "Your panels · sized to your windows";
        bwrap.appendChild(this.cutDiagram(d.nest, false, btitle, railW));
      }
      col.appendChild(bwrap);
    } else if (photos.length) {
      var ai = Math.min(this.activePhoto || 0, photos.length - 1);
      // Swipeable carousel: a horizontal scroll-snap track of every photo (mobile shows a peek of the next
      // slide; the thumbs below are a single-row film strip). Scroll <-> activePhoto <-> thumb selection are
      // synced WITHOUT a full re-render, so swiping/clicking stays smooth and never disturbs step focus.
      var track = el("div", "ezsf__gallery-track");
      track.setAttribute("role", "group");
      track.setAttribute("aria-label", esc((meta.label || "") + " images"));
      photos.forEach(function (ph, i) {
        var slide = el("div", "ezsf__gallery-slide");
        slide.style.backgroundImage = "url('" + ph + "')";
        // Studio detail shots (mesh / snap / kit — filename `…shade-detail…`) are square or portrait on a
        // light ground, so a cover crop slices the product. Show those CONTAIN on a light fill; lifestyle
        // heroes keep the default cover so they stay full-bleed.
        if (ph.indexOf("shade-detail") !== -1) { slide.style.backgroundSize = "contain"; slide.style.backgroundColor = "#f4f3f0"; }
        slide.setAttribute("role", "img");
        slide.setAttribute("aria-label", esc((meta.label || "") + ": Thermo Weave™ exterior shade, image " + (i + 1) + " of " + photos.length));
        track.appendChild(slide);
      });
      col.appendChild(track);
      if (photos.length > 1) {
        var thumbWrap = el("div", "ezsf__gallery-thumbwrap");
        var thumbs = el("div", "ezsf__gallery-thumbs");
        var selThumb = function (idx) {
          for (var j = 0; j < thumbs.children.length; j++) {
            var on = j === idx; thumbs.children[j].classList.toggle("is-sel", on);
            if (on) thumbs.scrollTo({ left: thumbs.children[j].offsetLeft - (thumbs.clientWidth - thumbs.children[j].clientWidth) / 2, behavior: "smooth" });
          }
        };
        photos.forEach(function (ph, i) {
          var t = el("button", "ezsf__gallery-thumb" + (ai === i ? " is-sel" : ""));
          t.type = "button";
          t.style.backgroundImage = "url('" + ph + "')";
          if (ph.indexOf("shade-detail") !== -1) { t.style.backgroundSize = "contain"; t.style.backgroundColor = "#f4f3f0"; }
          t.setAttribute("aria-label", "Show image " + (i + 1));
          t.addEventListener("click", function () {
            self.activePhoto = i;
            if (track.children[i]) track.scrollTo({ left: track.children[i].offsetLeft, behavior: "smooth" });
            selThumb(i);
          });
          thumbs.appendChild(t);
        });
        thumbWrap.appendChild(thumbs);
        // "more images" arrows on the THUMBNAIL row — back (‹) + forward (›). Each shows ONLY when there's
        // room to scroll that way (back appears once you've scrolled past the first thumb). With the partial
        // peek of the next thumb, they make it clear there are more images. On the thumbs, not the hero.
        var mkArrow = function (dir, label, poly) {
          var b = el("button", "ezsf__gallery-thumbarrow ezsf__gallery-thumbarrow--" + dir);
          b.type = "button"; b.setAttribute("aria-label", label);
          b.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="' + poly + '"></polyline></svg>';
          b.addEventListener("click", function () { thumbs.scrollBy({ left: (dir === "l" ? -1 : 1) * thumbs.clientWidth * 0.8, behavior: "smooth" }); });
          return b;
        };
        var arrowL = mkArrow("l", "Previous images", "15 6 9 12 15 18");
        var arrowR = mkArrow("r", "More images", "9 6 15 12 9 18");
        thumbWrap.appendChild(arrowL);
        thumbWrap.appendChild(arrowR);
        var updArrows = function () {
          arrowL.style.display = thumbs.scrollLeft <= 2 ? "none" : "flex";
          arrowR.style.display = (thumbs.scrollLeft + thumbs.clientWidth >= thumbs.scrollWidth - 2) ? "none" : "flex";
        };
        thumbs.addEventListener("scroll", function () { requestAnimationFrame(updArrows); }, { passive: true });
        requestAnimationFrame(updArrows);
        col.appendChild(thumbWrap);
        // scroll -> nearest slide -> activePhoto + thumb sel (debounced); keeps the film strip in view
        var stmr;
        track.addEventListener("scroll", function () {
          clearTimeout(stmr);
          stmr = setTimeout(function () {
            var kids = track.children, best = 0, bd = Infinity;
            for (var i = 0; i < kids.length; i++) { var dd = Math.abs(kids[i].offsetLeft - track.scrollLeft); if (dd < bd) { bd = dd; best = i; } }
            if (best !== self.activePhoto) { self.activePhoto = best; selThumb(best); }
          }, 70);
        }, { passive: true });
      }
      // restore scroll to the active slide after mount (a re-render rebuilds the DOM)
      if (ai > 0) requestAnimationFrame(function () { if (track.children[ai]) track.scrollLeft = track.children[ai].offsetLeft; });
    }
    // Fig. caption — master builder only. Removed on the directed shade PDPs (rv/home/skylight/boat)
    // per owner 2026-08-04: both the Fig. 01 photo caption and the Fig. 02 cut-diagram caption.
    if (!this.directed) {
      var fig = el("div", "ezsf__fig");
      if (this.activeView === "build" && canBuild) {
        fig.appendChild(el("span", "ezsf__fig-n", "Fig. 02"));
        fig.appendChild(el("span", "ezsf__fig-t", esc(
          this.style === "precut" ? "Ships precut to these sizes. Solid line is the shade, dashed is your window. Trim to fit."
          : this.style === "roll" ? "Cut whole from the roll, no seams. Solid line is the shade, dashed is your window."
          : "Solid line is the shade panel, dashed is your window opening. You pick precut or a roll next.")));
      } else {
        fig.appendChild(el("span", "ezsf__fig-n", "Fig. 01"));
        fig.appendChild(el("span", "ezsf__fig-t", esc((meta.label || "") + ": Thermo Weave™ exterior shade")));
      }
      col.appendChild(fig);
    }

    // includes card
    var inc = el("div", "ezsf__includes");
    inc.appendChild(el("div", "ezsf__includes-h", "Every order includes"));
    var rows = [
      ["Thermo Weave™ mesh", this.style === "roll" ? "6 ft roll, cut to fit" : this.style === "precut" ? "Precut to your window size" : "Precut to size, or a 6 ft roll"],
      // C1: lead with the no-drill benefit (universally true, unlike a frame-vs-glass claim, which is wrong for
      // glass/roof-mounted types), keep the format-aware "what you get" detail.
      ["No-drill 3M™ snaps", this.style === "roll" ? "Peel-and-stick 3M™ VHB, added to match your layout. No drilling." : this.style === "precut" ? "Peel-and-stick 3M™ VHB in the box: caps, pins and studs. No drilling." : "Peel-and-stick 3M™ VHB. No holes, no tools, no drilling."],
      ["Install & trim guide", "Step-by-step trim and mount instructions"],
      // Install consumables ride inside the snap pack (BOM: 1 pencil + 1 removal tool per pack, primer scales
      // with snap count). Any shade order WITH snaps ships them; a build can resolve to multiple packs, so the
      // wording is count-less. Shown as a general "every order includes" promise (configured orders always
      // carry snaps). (owner-approved 2026-08-03)
      ["Install kit", "Primer pen, marking pencil & removal tool"],
    ];
    rows.forEach(function (r) {
      var row = el("div", "ezsf__inc-row");
      row.appendChild(el("span", "ezsf__inc-tick"));
      var t = el("div");
      t.appendChild(el("div", "ezsf__inc-t", esc(r[0])));
      t.appendChild(el("div", "ezsf__inc-d", esc(r[1])));
      row.appendChild(t);
      inc.appendChild(row);
    });
    var badges = el("div", "ezsf__inc-badges");
    ["Up to 90% heat blocked", "Limited Lifetime Warranty"].forEach(function (btxt) {
      var b = el("span", "ezsf__inc-badge", esc(btxt)); badges.appendChild(b);
    });
    inc.appendChild(badges);
    col.appendChild(inc);
    return col;
  };

  // =========================================================================
  // BOOT — one instance per section.
  // =========================================================================
  function boot() {
    var mounts = document.querySelectorAll("[data-ez-shade-finder]");
    for (var i = 0; i < mounts.length; i++) {
      var m = mounts[i];
      if (m.__ezBooted) continue;
      var sid = m.getAttribute("data-ez-shade-finder");
      var store = window.__EZ_SHADE_CFG__ || {};
      var cfg = store[sid];
      if (!cfg) continue;
      m.__ezBooted = true;
      try { new ShadeFinder(m, cfg); }
      catch (e) { if (window.console) console.error("[ez-shade-finder]", e); }
    }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  document.addEventListener("shopify:section:load", boot);
})();
